import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, API, idr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import AttachmentPanel from "./finance/AttachmentPanel";
import BarcodeScanner from "../components/hris/BarcodeScanner";
import {
  ClipboardList, Plus, X, Truck, Gift, Download, Trash2, CheckCircle2, AlertTriangle,
  ListChecks, XCircle, Printer, Search, ShieldAlert, Eye, Pencil, Lock, Paperclip,
} from "lucide-react";
import FakturPrint from "../components/faktur/FakturPrint";
import PemilihTemplate from "../components/faktur/PemilihTemplate";
import { DatePicker } from "@/components/ui/date-picker";
import LoadingState from "@/components/ui/loading-state";
import { hariIniISO, hariIniDate, isoDariDate, formatTanggal } from "@/lib/tanggal";
import { useDebounce } from "@/hooks/useDebounce";
import {
  TabelDesktop, DaftarKartuMobile, KartuBaris, KartuBarisHeader, KartuBarisMeta, KartuBarisAksi,
} from "@/components/ui/responsive-table";
import { useModalGuard, useModalLayer, UnsavedGuardBar } from "../hooks/useModalGuard";
import { useSortState, sortRows, SortTh } from "@/components/ui/sort";

const STATUS = {
  terbuka: { l: "Menunggu Kiriman", c: "bg-amber-50 text-amber-700 border-amber-200" },
  sebagian: { l: "Diterima Sebagian", c: "bg-sky-50 text-sky-700 border-sky-200" },
  selesai: { l: "Selesai", c: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  batal: { l: "Dibatalkan", c: "bg-slate-100 text-slate-500 border-slate-200" },
  // Poin 7 (maker-checker): PO bernilai besar menunggu persetujuan sebelum
  // bisa menerima barang. Amber lebih gelap + ikon supaya beda kelihatan
  // dari "terbuka" biasa, bukan warna baru di luar palet slate+emerald+amber.
  menunggu_approval: { l: "Menunggu Approval", c: "bg-amber-100 text-amber-900 border-amber-300 font-semibold" },
};

// Status pengajuan Barang Tanpa Biaya (brief owner 14 Agu #5) — gerbang
// approval-nya SAMA persis dengan PO (require_approver), jadi warnanya
// mengikuti palet yang sama (amber = menunggu, bukan warna baru).
const STATUS_HIBAH = {
  menunggu_approval: { l: "Menunggu Approval", c: "bg-amber-100 text-amber-900 border-amber-300 font-semibold" },
  disetujui: { l: "Disetujui", c: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  ditolak: { l: "Ditolak", c: "bg-rose-50 text-rose-700 border-rose-200" },
};

// Role yang backend izinkan approve/tolak PO (routers/inventory/_shared.py::require_approver).
// Disalin di sini SUPAYA tombol Setujui/Tolak tidak pernah tampil untuk akun
// yang backend-nya pasti akan menolak 403 — permission "update" saja tidak
// cukup, backend menggerbangi ganda lewat role.
const APPROVER_ROLES = ["finance_spv", "finance_ceo", "admin", "superadmin"];

const SUMBER = ["hibah", "sitaan", "hadiah", "inventaris", "lainnya"];

// Cara bayar yang TERSIMPAN di PO/penerimaan, untuk TAMPILAN saja (brief 22 Sep
// 2026, H1). PO baru hanya lahir sebagai "utang" (tagihan, dilunasi tim Finance)
// atau "tradein" (penanda Barang Trade-in). "kas"/"bank" hanya ada di PO LAMA —
// tetap ditampilkan apa adanya, nilai yang tidak dikenal jatuh ke teks mentahnya.
// "tanpa_biaya" = penerimaan Barang Tanpa Biaya (hibah/sitaan/dll) di tab yang sama.
const LABEL_CARA_BAYAR = { utang: "Tagihan", tradein: "Trade-in", kas: "Kas", bank: "Bank", tanpa_biaya: "Tanpa biaya" };
const labelCaraBayar = (m) => LABEL_CARA_BAYAR[m] || m || "-";

function infoPembayaranPO(po) {
  const m = po?.metode_bayar || "utang";
  if (m === "tradein") return "Barang Trade-in — lunas otomatis dari penampung trade-in cabang saat diterima";
  if (m === "kas" || m === "bank") return `PO lama: dibayar dari ${labelCaraBayar(m)} saat barang diterima`;
  return "Tagihan dilunasi tim Finance (Finance › Pelunasan Pembelian)";
}

// PPN (keputusan owner 18 Agu 2026, CV ZOA berstatus PKP): 11% dari DPP,
// SELALU ditambahkan terpisah dari harga yang diketik staf (harga input
// TIDAK PERNAH termasuk PPN). Konstanta ini HANYA untuk pratinjau live di
// form sebelum simpan — nilai yang BENAR-BENAR tersimpan datang dari
// `hitung_ppn()` di backend (satu sumber kebenaran, dibulatkan di situ supaya
// tidak ada jurnal selisih 1 rupiah). Tarif per dokumen dikunci ke nilai saat
// dibuat; kalau tarif PPN berubah di masa depan, dokumen lama TIDAK dihitung
// ulang dari konstanta ini.
const PPN_PERSEN_PREVIEW = 11;

// Poin 2: paginasi server-side sungguhan untuk daftar PO (pola YANG SAMA
// dengan GET /inventory/products, lihat routers/inventory/products.py) —
// dulu halaman ini minta `limit` tetap (maks 1000 yang backend izinkan) lalu
// menampilkan spanduk peringatan begitu hasilnya pas di batas itu; itu cuma
// menambal gejalanya (PO lebih lama tetap lenyap diam-diam, tidak ada cara
// membukanya). Sekarang backend mengembalikan `total`/`total_pages` dan
// frontend menavigasi halaman sungguhan lewat `page`/`per_page`.
const PO_PER_PAGE = 50;

export default function InventoryPO() {
  const { can, user } = useAuth();
  // Daftar gudang dipakai untuk dropdown tujuan, tapi /inventory/warehouses memakai izin
  // inventory_gudang — modul yang berbeda dari inventory_po yang menggerbangi menu ini.
  // Kalau tetap dipanggil, satu 403 menggagalkan Promise.all dan seluruh halaman Pembelian
  // ikut kosong padahal PO-nya sendiri boleh dilihat.
  const bolehGudang = can("inventory_gudang", "view");
  // Daftar template cetak (menu Template Faktur & Struk) memakai izin modul
  // "faktur" — terpisah dari inventory_po yang menggerbangi halaman ini.
  // Tanpa izin ini, pemilih template dilewati dan backend memilih template
  // default seperti biasa (perilaku lama, tetap jalan).
  const bolehFaktur = can("faktur", "view");
  // PO LAMA kas/bank mengeluarkan uang saat diterima; backend
  // (`purchase.py::terima_barang`) menuntut hak melunasi Finance
  // (`finance_utang.update`) untuk menerimanya — brief 22 Sep H1: admin
  // inventory tidak melunasi pembelian. Tombol Terima mengikuti kunci yang sama.
  const bolehMelunasi = can("finance_utang", "update");
  const poLamaUang = (p) => ["kas", "bank"].includes(p?.metode_bayar);
  const bisaTerimaPO = (p) => ["terbuka", "sebagian"].includes(p.status) && can("inventory_po", "create")
    && (!poLamaUang(p) || bolehMelunasi);
  const terimaPOLamaTertahan = (p) => ["terbuka", "sebagian"].includes(p.status) && can("inventory_po", "create")
    && poLamaUang(p) && !bolehMelunasi;
  const [tab, setTab] = useState("po");
  const [searchParams, setSearchParams] = useSearchParams();
  const [pos, setPos] = useState([]);
  // Pengajuan Barang Tanpa Biaya (brief owner 14 Agu #5) — TERPISAH dari
  // `terima` (yang cuma memuat penerimaan yang SUDAH final/disetujui, lihat
  // GET /inventory/penerimaan): daftar ini memuat SEMUA status termasuk yang
  // masih menunggu approval, supaya approver punya tempat memprosesnya.
  const [hibahPengajuan, setHibahPengajuan] = useState([]);
  const [terima, setTerima] = useState([]);
  const [tagihan, setTagihan] = useState({ items: [], total_belum_dibayar: 0 });
  const [gudang, setGudang] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  // Poin 14c (brief owner 23 Agu 2026) — Draf PO: `formDraftId` menandai
  // FormPO SEDANG melanjutkan draf yang sudah tersimpan (bukan bikin PO
  // baru dari nol) — null berarti "PO Baru" biasa. `drafList` dimuat
  // TERPISAH dari `pos` (GET /inventory/po SENGAJA mengecualikan draf,
  // lihat purchase.py::list_po) supaya draf punya tab/daftar sendiri,
  // tidak pernah campur ke daftar PO biasa.
  const [formDraftId, setFormDraftId] = useState(null);
  const [drafList, setDrafList] = useState([]);
  const [drafLoading, setDrafLoading] = useState(true);
  const [hibah, setHibah] = useState(null);
  const [terimaFor, setTerimaFor] = useState(null);
  const [busy, setBusy] = useState(false);
  // Poin 12 — View/Edit PO: {po, loading} untuk modal pratinjau (GET
  // /po/{id}, sudah mengembalikan penerimaan[] + units_diterima per item —
  // lihat detail_po di purchase.py), dan `editPO` untuk form edit yang
  // dibuka DARI pratinjau itu (bentuknya sama dengan `form` di atas, plus
  // `qty_diterima`/`po_id`/`status_awal` per baris untuk mengunci UI).
  const [previewPO, setPreviewPO] = useState(null);
  const [editPO, setEditPO] = useState(null);
  // {po, step, templates, templateId, loading, data} — brief #18 cetak faktur
  // pembelian + pemilih template. step: "memuat_template" | "pilih_template" |
  // "kosong" | "preview".
  const [cetakPO, setCetakPO] = useState(null);
  // ---- Daftar PO: fetch terpisah dari sisa halaman supaya pencarian tidak
  // perlu memuat ulang tab Penerimaan/Tagihan/Gudang tiap kali diketik. ----
  const [poQuery, setPoQuery] = useState("");
  const poQueryDebounced = useDebounce(poQuery, 400);
  const [poPage, setPoPage] = useState(1);
  const [poMeta, setPoMeta] = useState({ total: 0, total_pages: 1 });
  const [poLoading, setPoLoading] = useState(true);
  // Lampiran fisik PO/DO (brief owner 29 Agu, Inven poin 1) — memakai mesin
  // lampiran yang sudah ada, bukan penyimpanan berkas kedua.
  const [lampiranPO, setLampiranPO] = useState(null);
  const [poError, setPoError] = useState(null);
  const poReqSeq = useRef(0);
  // Pengurutan tabel (client-side, lihat components/ui/sort.jsx) — urutan
  // bawaan tetap seperti sebelumnya. Tab "po" backend-nya SUDAH paginasi
  // server-side (50/halaman) — sort di sini HANYA menata ulang 50 baris di
  // halaman yang sedang dibuka, bukan seluruh PO. Tab hibah/terima/tagihan
  // masing-masing backend-nya juga terpotong `.to_list(300..500)`.
  const poSort = useSortState(null, "asc");
  const hibahSort = useSortState(null, "asc");
  const terimaSort = useSortState(null, "asc");
  const tagihanSort = useSortState(null, "asc");

  const loadPO = async (q, page) => {
    const seq = ++poReqSeq.current;
    setPoLoading(true);
    setPoError(null);
    try {
      const r = await api.get("/inventory/po", {
        params: { page, per_page: PO_PER_PAGE, ...(q ? { q } : {}) },
      });
      if (seq !== poReqSeq.current) return; // respons basi (pencarian/halaman lanjutan sudah menembak duluan)
      setPos(r.data.items || []);
      setPoMeta({ total: r.data.total || 0, total_pages: r.data.total_pages || 1 });
    } catch (e) {
      if (seq !== poReqSeq.current) return;
      const msg = e?.response?.data?.detail || "Gagal memuat daftar PO";
      toast.error(msg);
      setPoError(msg);
    } finally {
      if (seq === poReqSeq.current) setPoLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadPO(poQueryDebounced.trim(), poPage); }, [poQueryDebounced, poPage]);

  const load = async () => {
    setLoading(true);
    try {
      const [b, c, d, e, f] = await Promise.all([
        api.get("/inventory/penerimaan"),
        api.get("/inventory/tagihan"),
        bolehGudang ? api.get("/inventory/warehouses") : null,
        // Supplier terdaftar untuk dropdown form PO. Gagal memuat bukan alasan
        // memblokir halaman — form akan menampilkan arahan tambah cepat.
        api.get("/supplier").catch(() => null),
        api.get("/inventory/penerimaan-non-pembelian"),
      ]);
      setTerima(b.data.items || []);
      setTagihan(c.data);
      if (d) setGudang(d.data.items || []);
      if (e) setSuppliers(e.data.items || []);
      setHibahPengajuan(f.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat data");
    } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  // Poin 14c — daftar draf TERPISAH dari `loadPO` (tab sendiri, endpoint
  // sendiri: `GET /inventory/po/draf`, scope "milik sendiri" untuk non-approver
  // atau "semua" untuk approver — ditegakkan backend, lihat `_cek_akses_draf`).
  const loadDraf = async () => {
    setDrafLoading(true);
    try {
      const r = await api.get("/inventory/po/draf");
      setDrafList(r.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat daftar draf PO");
    } finally { setDrafLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadDraf(); }, []);

  const muatUlangSemua = async () => {
    await Promise.all([load(), loadPO(poQueryDebounced.trim(), poPage), loadDraf()]);
  };

  const kirim = async (fn, pesan) => {
    setBusy(true);
    try {
      const r = await fn();
      toast.success(r?.data?.message || pesan);
      // Poin 4: peringatan_harga TIDAK memblokir simpan — tampil sebagai
      // peringatan (kuning), bukan error, dan tidak menghalangi apa pun yang
      // sudah terjadi (PO/penerimaan sudah tersimpan saat toast ini muncul).
      (r?.data?.peringatan_harga || []).forEach(p => toast.warning(p, { duration: 8000 }));
      await muatUlangSemua();
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memproses");
      return false;
    } finally { setBusy(false); }
  };

  // Brief A.6 meminta export "dengan memilih periode" — dulu di-hardcode 30 hari
  // terakhir padahal backend sudah menerima rentang bebas; pengguna cuma tidak
  // pernah diberi cara memilihnya.
  const [exportRange, setExportRange] = useState(() => {
    const tglDari = hariIniDate();
    tglDari.setDate(tglDari.getDate() - 30);
    return { dari: isoDariDate(tglDari), sampai: hariIniISO() };
  });
  const exportExcel = async () => {
    const { dari, sampai } = exportRange;
    if (!dari || !sampai) { toast.error("Isi tanggal awal dan akhir dulu ya"); return; }
    if (dari > sampai) { toast.error("Tanggal awal tidak boleh melewati tanggal akhir"); return; }
    try {
      const token = localStorage.getItem("hris_token");
      const res = await fetch(
        `${API}/inventory/penerimaan/export?date_from=${dari}&date_to=${sampai}`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = window.URL.createObjectURL(blob);
      a.download = `Penerimaan_${dari}_${sampai}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(a.href);
    } catch { toast.error("Gagal mengunduh Excel"); }
  };

  const batalPO = async (po) => {
    const alasan = window.prompt(`Alasan membatalkan PO ${po.code}?`);
    if (!alasan) return;
    await kirim(() => api.post(`/inventory/po/${po.id}/batal`, { reason: alasan }), "PO dibatalkan");
  };

  // Poin 5 (maker-checker): approve/tolak dijaga GANDA sama seperti backend —
  // permission "update" DAN role approver. `catatan` opsional (backend tidak
  // mewajibkan), jadi prompt kosong tetap boleh lanjut; hanya Cancel (null)
  // yang membatalkan aksinya.
  const bisaApprove = can("inventory_po", "update") && APPROVER_ROLES.includes(user?.role);
  const approvePO = async (po) => {
    const catatan = window.prompt(`Setujui PO ${po.code}? Catatan opsional (boleh dikosongkan):`, "");
    if (catatan === null) return;
    await kirim(() => api.post(`/inventory/po/${po.id}/approve`, { catatan }), `PO ${po.code} disetujui`);
  };
  const tolakApprovalPO = async (po) => {
    const alasan = window.prompt(`Alasan menolak PO ${po.code}?`);
    if (!alasan) return;
    await kirim(() => api.post(`/inventory/po/${po.id}/tolak-approval`, { reason: alasan }), `PO ${po.code} ditolak`);
  };

  // Poin 12 — View: modal pratinjau. `detail_po` (backend) sudah menyatukan
  // PO + `penerimaan[]` + `units_diterima` per baris ber-IMEI dalam SATU
  // panggilan — tidak perlu menyusun ulang di sini.
  const [previewLoading, setPreviewLoading] = useState(false);
  const bukaPreview = async (po) => {
    setPreviewLoading(true);
    try {
      const { data } = await api.get(`/inventory/po/${po.id}`);
      setPreviewPO(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat detail PO");
    } finally { setPreviewLoading(false); }
  };

  // Gerbang tombol Edit di frontend SAMA dengan backend (`edit_po`):
  // permission "update" DAN role approver — bukan sekadar `can()`, supaya
  // tombol tidak tampil untuk akun yang backend-nya pasti menolak 403
  // (aturan CLAUDE.md #3).
  const bisaEditPO = bisaApprove;
  // A3 (brief 4 Sep 2026): tautan dari Finance > Utang membawa `?po=<id>` —
  // buka pratinjau PO itu langsung supaya staf bisa mencocokkan tagihan tanpa
  // mencari ulang. Param dibersihkan setelah dibuka agar refresh/Back tidak
  // membukanya lagi. Ditaruh SESUDAH `bukaPreview` didefinisikan.
  useEffect(() => {
    const poId = searchParams.get("po");
    if (!poId) return;
    // `bukaPreview` mengambil detailnya sendiri dari `po.id` (dan memberi
    // pesan galat kalau tidak ada) — jangan mengambil dua kali di sini.
    bukaPreview({ id: poId });
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const bukaEditPO = (po) => {
    // Salin field yang sama dengan form Create; qty_diterima per baris
    // dibawa TERPISAH (tidak dikirim ke backend, `ItemInput` tidak
    // mengenalnya) HANYA untuk mengunci UI (qty minimum, harga read-only).
    setEditPO({
      id: po.id, code: po.code, status: po.status,
      tanggal: po.tanggal, supplier_id: po.supplier_id, supplier_nama: po.supplier_nama,
      // `metode_bayar`/`barang_tradein` dibawa HANYA untuk tampilan: Edit PO
      // mengunci keduanya (backend `edit_po`), dan `bersihkanEditPO` sengaja
      // tidak mengirimnya sama sekali.
      warehouse_id: po.warehouse_id, skema: po.skema, metode_bayar: po.metode_bayar,
      barang_tradein: !!po.barang_tradein,
      tanggal_jatuh_tempo: po.tanggal_jatuh_tempo || "",
      no_po_supplier: po.no_po_supplier || "", catatan: po.catatan || "", kena_ppn: !!po.kena_ppn,
      // P9: kalau satu unit saja sudah bercatatan, kolom catatannya ditampilkan.
      catatan_unit: (po.items || []).some(it => (it.units || []).some(u => u.catatan)),
      qty_diterima_total: po.qty_diterima || 0,
      items: (po.items || []).map(it => ({
        sku: it.sku, qty: it.qty, harga_per_unit: it.harga_per_unit,
        units: (it.units || []).map(u => ({ identifier: u.identifier, harga: u.harga, catatan: u.catatan || "" })),
        satuan_input: "kecil",
        qty_diterima: it.qty_diterima || 0,   // UI-only, dibuang saat kirim (lihat bersihkanEditPO)
      })),
    });
    setPreviewPO(null);
  };
  const simpanEditPO = async () => {
    if (!editPO.supplier_id) {
      toast.error("Pilih supplier dari daftar dulu (atau tambah cepat) — supplier bebas ketik tidak diterima lagi");
      return;
    }
    // Jatuh tempo TIDAK diwajibkan lagi di sini (P10/E17 poin 3): termin supplier
    // sering belum final saat memesan; kewajibannya di form Terima, titik
    // utangnya lahir — backend `terima_barang` & `_selesaikan_penerimaan_po`.
    const ok = await kirim(() => api.put(`/inventory/po/${editPO.id}`, bersihkanEditPO(editPO)), "PO diperbarui");
    if (ok) setEditPO(null);
  };

  // Approval Barang Tanpa Biaya (brief owner 14 Agu #5) — gerbang GANDA SAMA
  // persis dengan PO (`bisaApprove` di atas): permission `inventory_po.update`
  // DAN role approver. Backend juga menolak pengaju yang mencoba menyetujui/
  // menolak pengajuannya sendiri (kecuali superadmin/admin lewat jalur
  // darurat) — tombolnya tetap ditampilkan ke approver lain karena tidak ada
  // cara murah mengetahui di frontend siapa pengaju vs siapa yang login tanpa
  // membandingkan email, jadi kalau memang pengaju sendiri, backend membalas
  // 403 dengan pesan yang jelas (ditampilkan lewat toast oleh `kirim()`).
  const approveHibah = async (p) => {
    const catatan = window.prompt(
      `Setujui barang tanpa biaya ${p.code}? Stok & jurnal akan terbentuk sekarang. Catatan opsional:`, "");
    if (catatan === null) return;
    await kirim(() => api.post(`/inventory/penerimaan-non-pembelian/${p.id}/approve`, { catatan }),
               `Pengajuan ${p.code} disetujui`);
  };
  const tolakHibah = async (p) => {
    const alasan = window.prompt(`Alasan menolak pengajuan ${p.code}?`);
    if (!alasan) return;
    await kirim(() => api.post(`/inventory/penerimaan-non-pembelian/${p.id}/tolak`, { reason: alasan }),
               `Pengajuan ${p.code} ditolak`);
  };

  // Cetak Faktur Pembelian (brief #18) — data BACA-SAJA. Kalau jenis
  // "pembelian" punya lebih dari satu template, pengguna memilih dulu;
  // kalau cuma satu (atau tidak punya izin menelusuri daftar template),
  // langsung dipakai seperti sebelumnya.
  const bukaCetakPO = async (po) => {
    setCetakPO({ po, step: "memuat_template", templates: [], templateId: null, loading: true, data: null });
    if (!bolehFaktur) { await muatDokumenPO(po, null); return; }
    try {
      // warehouse_id (brief bos): hanya tampilkan template "pembelian" yang
      // memang diizinkan untuk gudang tujuan PO ini — lihat
      // `_template_allows_warehouse` di faktur.py.
      const { data } = await api.get("/faktur/templates", { params: { jenis: "pembelian", warehouse_id: po.warehouse_id || undefined } });
      const daftar = data?.items || [];
      if (daftar.length === 0) {
        setCetakPO({ po, step: "kosong", templates: [], loading: false, data: null });
        return;
      }
      if (daftar.length === 1) { await muatDokumenPO(po, daftar[0]); return; }
      const def = daftar.find(t => t.is_default) || daftar[0];
      setCetakPO({ po, step: "pilih_template", templates: daftar, templateId: def.id, loading: false, data: null });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat daftar template cetak");
      setCetakPO(null);
    }
  };

  const muatDokumenPO = async (po, template) => {
    setCetakPO(m => ({ ...(m || { po }), po, step: "preview", loading: true, data: null }));
    try {
      const { data } = await api.get(`/faktur/data/pembelian/${po.id}`, {
        params: template ? { template_id: template.id } : {},
      });
      setCetakPO(m => (m ? { ...m, step: "preview", loading: false, data } : m));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat faktur pembelian");
      setCetakPO(null);
    }
  };

  const jumlahMenungguApproval = pos.filter(p => p.status === "menunggu_approval").length;
  const jumlahHibahMenunggu = hibahPengajuan.filter(p => p.status === "menunggu_approval").length;

  return (
    <div className="p-4 md:p-8 space-y-5" data-testid="inventory-po">
      <div className="flex items-start justify-between flex-col lg:flex-row gap-3">
        <div>
          <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">Inventory</p>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mt-1">
            Pembelian & Penerimaan
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            PO hanya dokumen pesanan — stok dan utang baru muncul saat barang benar-benar diterima.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1.5">
            <DatePicker value={exportRange.dari} data-testid="inv-po-export-dari"
                        onChange={v => setExportRange(s => ({ ...s, dari: v }))} className="w-36" />
            <span className="text-slate-400 text-xs">s/d</span>
            <DatePicker value={exportRange.sampai} data-testid="inv-po-export-sampai"
                        onChange={v => setExportRange(s => ({ ...s, sampai: v }))} className="w-36" />
          </div>
          <button onClick={exportExcel} data-testid="inv-po-export"
                  className="h-10 px-4 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-sm font-medium text-slate-700 inline-flex items-center gap-2">
            <Download className="w-4 h-4" /> Export Excel
          </button>
          {/* Tanpa daftar gudang, form PO tidak punya gudang tujuan untuk dipilih dan
              penyimpanannya pasti ditolak backend — lebih baik tombolnya tidak muncul. */}
          {can("inventory_po", "create") && bolehGudang && (
            <>
              <button onClick={() => setHibah({
                tanggal: hariIniISO(), warehouse_id: gudang[0]?.id || "", sumber: "hibah",
                asal_keterangan: "", skema: "new", items: [{ sku: "", qty: 1, harga_per_unit: "" }],
              })} data-testid="inv-po-hibah"
                      className="h-10 px-4 rounded-lg border border-amber-300 bg-amber-50 hover:bg-amber-100 text-sm font-medium text-amber-800 inline-flex items-center gap-2">
                <Gift className="w-4 h-4" /> Barang Tanpa Biaya
              </button>
              <button onClick={() => {
                setFormDraftId(null);   // poin 14c: PO Baru BUKAN lanjutan draf
                setForm({
                  tanggal: hariIniISO(), supplier_id: "", supplier_nama: "", warehouse_id: gudang[0]?.id || "",
                  // Tanpa cara bayar (brief 22 Sep 2026, H1) — PO baru selalu jadi
                  // tagihan Finance, kecuali dicentang Barang Trade-in.
                  skema: "new", barang_tradein: false, tanggal_jatuh_tempo: "",
                  no_po_supplier: "", catatan: "", kena_ppn: false, catatan_unit: false,
                  items: [{ sku: "", qty: 1, harga_per_unit: "", units: [] }],
                });
              }} data-testid="inv-po-add"
                      className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium inline-flex items-center gap-2">
                <Plus className="w-4 h-4" /> PO Baru
              </button>
            </>
          )}
        </div>
      </div>

      {!loading && !bolehGudang && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800 flex items-start gap-2"
             data-testid="inv-po-noizin-gudang">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          Akun Anda belum punya izin modul Gudang, jadi PO baru tidak bisa dibuat dari sini —
          daftar PO, penerimaan, dan tagihan tetap bisa dibaca.
        </div>
      )}
      {gudang.length === 0 && !loading && bolehGudang && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800 flex items-start gap-2"
             data-testid="inv-po-nogudang">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          Belum ada gudang. Buat gudang dulu di menu Gudang & Cabang — barang tidak bisa diterima tanpa tempat menyimpannya.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <Kartu label="PO Terbuka" nilai={pos.filter(p => ["terbuka", "sebagian"].includes(p.status)).length}
               sub="menunggu kiriman" />
        <Kartu label="Menunggu Approval" nilai={jumlahMenungguApproval} sub="perlu persetujuan dulu"
               tone={jumlahMenungguApproval > 0 ? "text-amber-700" : undefined} />
        <Kartu label="Penerimaan Tercatat" nilai={terima.length} sub="dokumen DO" />
        <Kartu label="Tagihan Belum Dibayar" nilai={idr(tagihan.total_belum_dibayar || 0)}
               sub="dilunasi tim Finance › Pelunasan Pembelian" tone="text-rose-700" />
        <Kartu label="Tanpa Biaya Menunggu" nilai={jumlahHibahMenunggu} sub="hibah/sitaan/dll perlu approval"
               tone={jumlahHibahMenunggu > 0 ? "text-amber-700" : undefined} />
        {/* Poin 14c — dihitung dari `drafList` (endpoint TERPISAH), BUKAN dari
            `pos` (yang sudah mengecualikan draf, lihat purchase.py::list_po) —
            supaya draf tetap punya tempatnya sendiri di dashboard tanpa ikut
            mencemari hitungan "PO Terbuka" di atas. */}
        <Kartu label="Draf PO" nilai={drafList.length} sub="coretan, belum jadi PO" />
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-1 flex-wrap">
          {[["po", "Purchase Order"], ["hibah", "Tanpa Biaya"], ["terima", "Penerimaan"], ["tagihan", "Tagihan"], ["stokmenipis", "Stok Menipis"], ["draf", "Draf PO"]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`inv-po-tab-${k}`}
                    className={`px-3 h-8 rounded-lg text-xs font-medium ${tab === k ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
              {l}
            </button>
          ))}
        </div>

        {tab === "po" && (
          <div className="px-4 pt-3 flex items-center gap-3 flex-wrap">
            <div className="relative w-full sm:w-80">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              {/* Reset ke halaman 1 SETIAP kali pencarian diketik ulang (batch
                  bareng setPoQuery, pola yang sama dengan InventoryProducts.jsx)
                  — kalau tidak, pencarian baru bisa "nyangkut" di halaman 5
                  hasil pencarian sebelumnya dan tampak kosong. */}
              <input value={poQuery} onChange={e => { setPoPage(1); setPoQuery(e.target.value); }}
                     data-testid="inv-po-search"
                     placeholder="Cari kode PO, No. PO supplier, atau nama supplier..."
                     className="h-9 w-full pl-9 pr-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
            </div>
            {!poLoading && !poError && (
              <p className="text-[11px] text-slate-500" data-testid="inv-po-total">
                {poMeta.total.toLocaleString("id-ID")} PO
              </p>
            )}
          </div>
        )}

        {tab === "po" ? (
          <>
            <TabelDesktop>
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr className="text-left">
                    <SortTh label="Kode / Tanggal" sortKeyName="tanggal" sortKey={poSort.sortKey} sortDir={poSort.sortDir} onSort={poSort.toggle} testid="inv-po-sort-tanggal" />
                    <SortTh label="Supplier" sortKeyName="supplier_nama" sortKey={poSort.sortKey} sortDir={poSort.sortDir} onSort={poSort.toggle} testid="inv-po-sort-supplier" />
                    <th className="px-3 py-2.5 font-semibold">Gudang</th>
                    <th className="px-3 py-2.5 font-semibold">Skema</th>
                    <th className="px-3 py-2.5 font-semibold text-right">Progres</th>
                    <SortTh label="Nilai" sortKeyName="nilai_total" sortKey={poSort.sortKey} sortDir={poSort.sortDir} onSort={poSort.toggle} align="right" testid="inv-po-sort-nilai" />
                    <th className="px-3 py-2.5 font-semibold">Status</th>
                    <th className="px-3 py-2.5 font-semibold text-right">Aksi</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  <LoadingState loading={poLoading} error={poError} kosong={!poLoading && !poError && pos.length === 0}
                                onRetry={() => loadPO(poQueryDebounced.trim(), poPage)} varian="tabel" kolom={8}
                                pesanKosong={poQuery ? "Tidak ada PO yang cocok dengan pencarian" : "Belum ada PO"}
                                data-testid="inv-po-tabel-state">
                    {sortRows(pos, poSort.sortKey, poSort.sortDir).map(p => {
                      const st = STATUS[p.status] || STATUS.terbuka;
                      return (
                        <tr key={p.id} data-testid={`inv-po-row-${p.id}`} className="hover:bg-slate-50/60">
                          <td className="px-3 py-2.5">
                            <p className="font-mono text-[11px] text-slate-500">{p.code}</p>
                            <p className="text-slate-800">{formatTanggal(p.tanggal)}</p>
                          </td>
                          <td className="px-3 py-2.5 text-slate-800">{p.supplier_nama}</td>
                          <td className="px-3 py-2.5 text-slate-600">{p.warehouse_nama}</td>
                          <td className="px-3 py-2.5">
                            <span className="text-[11px] px-2 py-0.5 rounded border bg-slate-50 border-slate-200 text-slate-700">
                              {p.skema === "second" ? "Second (harga per unit)" : "Baru"}
                            </span>
                          </td>
                          <td className="px-3 py-2.5 text-right text-slate-700">
                            {p.qty_diterima}/{p.qty_total}
                          </td>
                          <td className="px-3 py-2.5 text-right font-mono text-slate-900">{idr(p.nilai_total)}</td>
                          <td className="px-3 py-2.5">
                            <span className={`text-[11px] px-2 py-0.5 rounded border inline-flex items-center gap-1 ${st.c}`}>
                              {p.status === "menunggu_approval" && <ShieldAlert className="w-3 h-3" />}
                              {st.l}
                            </span>
                            {p.status === "menunggu_approval" && (
                              <p className="text-[10px] text-amber-700 mt-1 max-w-[160px]">
                                Menunggu persetujuan sebelum bisa menerima barang
                              </p>
                            )}
                            {terimaPOLamaTertahan(p) && (
                              <p className="text-[10px] text-slate-500 mt-1 max-w-[180px]" data-testid={`inv-po-lama-finance-${p.id}`}>
                                PO lama dibayar {labelCaraBayar(p.metode_bayar)} saat diterima — penerimaannya oleh tim Finance
                              </p>
                            )}
                          </td>
                          <td className="px-3 py-2.5">
                            <div className="flex items-center justify-end gap-1 flex-wrap">
                              {p.status === "menunggu_approval" && bisaApprove && (
                                <>
                                  <button onClick={() => approvePO(p)} data-testid={`inv-po-approve-${p.id}`}
                                          className="px-2 h-8 rounded bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-medium inline-flex items-center gap-1">
                                    <CheckCircle2 className="w-3.5 h-3.5" /> Setujui
                                  </button>
                                  <button onClick={() => tolakApprovalPO(p)} data-testid={`inv-po-tolak-approval-${p.id}`}
                                          className="px-2 h-8 rounded bg-rose-50 hover:bg-rose-100 text-rose-700 text-xs font-medium inline-flex items-center gap-1">
                                    <XCircle className="w-3.5 h-3.5" /> Tolak
                                  </button>
                                </>
                              )}
                              {bisaTerimaPO(p) && (
                                <button onClick={() => setTerimaFor(p)} data-testid={`inv-po-terima-${p.id}`}
                                        className="px-2 h-8 rounded bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-medium inline-flex items-center gap-1">
                                  <Truck className="w-3.5 h-3.5" /> Terima
                                </button>
                              )}
                              {/* §43: backend `batal_po` menuntut require_approver DI ATAS
                                  izin update. Tanpa cek peran di sini, staf ber-izin
                                  update melihat tombol yang pasti berujung 403. */}
                              {p.status === "terbuka" && bisaApprove && (
                                <button onClick={() => batalPO(p)} data-testid={`inv-po-batal-${p.id}`}
                                        className="w-8 h-8 rounded hover:bg-rose-50 text-rose-600 grid place-items-center">
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                              {/* Poin 12 — View: pratinjau PO + penerimaan[] sebelum edit/batal. */}
                              <button onClick={() => bukaPreview(p)} data-testid={`inv-po-lihat-${p.id}`}
                                      title="Lihat detail PO"
                                      className="w-8 h-8 rounded hover:bg-slate-100 text-slate-600 grid place-items-center">
                                <Eye className="w-4 h-4" />
                              </button>
                              <button onClick={() => bukaCetakPO(p)} data-testid={`btn-cetak-po-${p.id}`}
                                      title="Cetak Faktur Pembelian"
                                      className="w-8 h-8 rounded hover:bg-slate-100 text-slate-600 grid place-items-center">
                                <Printer className="w-4 h-4" />
                              </button>
                              {/* Lampiran fisik: foto surat jalan / faktur supplier,
                                  supaya pencocokan dokumen tidak perlu mengubek map. */}
                              <button onClick={() => setLampiranPO(p)} data-testid={`inv-po-lampiran-${p.id}`}
                                      title="Lampiran foto/berkas fisik"
                                      className="w-8 h-8 rounded hover:bg-slate-100 text-slate-600 grid place-items-center">
                                <Paperclip className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </LoadingState>
                </tbody>
              </table>
            </TabelDesktop>

            {lampiranPO && (
              <AttachmentPanel module="inventory_po" objId={lampiranPO.id}
                objLabel={`${lampiranPO.code} · ${lampiranPO.supplier_nama}`}
                onClose={() => setLampiranPO(null)} />
            )}

            <DaftarKartuMobile testid="inv-po-kartu">
              <LoadingState loading={poLoading} error={poError} kosong={!poLoading && !poError && pos.length === 0}
                            onRetry={() => loadPO(poQueryDebounced.trim(), poPage)} varian="kartu"
                            pesanKosong={poQuery ? "Tidak ada PO yang cocok dengan pencarian" : "Belum ada PO"}
                            data-testid="inv-po-kartu-state">
                {sortRows(pos, poSort.sortKey, poSort.sortDir).map(p => {
                  const st = STATUS[p.status] || STATUS.terbuka;
                  return (
                    <KartuBaris key={p.id} testid={`inv-po-card-${p.id}`}>
                      <KartuBarisHeader
                        kiri={<>
                          <p className="font-mono text-[11px] text-slate-500">{p.code}</p>
                          <p className="text-sm font-semibold text-slate-900">{p.supplier_nama}</p>
                          <p className="text-xs text-slate-500 mt-0.5">{formatTanggal(p.tanggal)} · {p.warehouse_nama}</p>
                        </>}
                        kanan={
                          <span className={`text-[11px] px-2 py-0.5 rounded border inline-flex items-center gap-1 ${st.c}`}>
                            {p.status === "menunggu_approval" && <ShieldAlert className="w-3 h-3" />}
                            {st.l}
                          </span>
                        }
                      />
                      {p.status === "menunggu_approval" && (
                        <p className="text-[10px] text-amber-700">Menunggu persetujuan sebelum bisa menerima barang</p>
                      )}
                      <KartuBarisMeta label="Skema" value={p.skema === "second" ? "Second (harga per unit)" : "Baru"} />
                      <KartuBarisMeta label="Progres" value={`${p.qty_diterima}/${p.qty_total}`} />
                      <KartuBarisMeta label="Nilai" value={idr(p.nilai_total)} tebal />
                      <KartuBarisAksi>
                        {p.status === "menunggu_approval" && bisaApprove && (
                          <>
                            <button onClick={() => approvePO(p)} data-testid={`inv-po-approve-${p.id}-m`}
                                    className="px-2.5 h-8 rounded bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-medium inline-flex items-center gap-1">
                              <CheckCircle2 className="w-3.5 h-3.5" /> Setujui
                            </button>
                            <button onClick={() => tolakApprovalPO(p)} data-testid={`inv-po-tolak-approval-${p.id}-m`}
                                    className="px-2.5 h-8 rounded bg-rose-50 hover:bg-rose-100 text-rose-700 text-xs font-medium inline-flex items-center gap-1">
                              <XCircle className="w-3.5 h-3.5" /> Tolak
                            </button>
                          </>
                        )}
                        {terimaPOLamaTertahan(p) && (
                          <p className="text-[10px] text-slate-500 w-full" data-testid={`inv-po-lama-finance-${p.id}-m`}>
                            PO lama dibayar {labelCaraBayar(p.metode_bayar)} saat diterima — penerimaannya oleh tim Finance
                          </p>
                        )}
                        {bisaTerimaPO(p) && (
                          <button onClick={() => setTerimaFor(p)} data-testid={`inv-po-terima-${p.id}-m`}
                                  className="px-2.5 h-8 rounded bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-medium inline-flex items-center gap-1">
                            <Truck className="w-3.5 h-3.5" /> Terima
                          </button>
                        )}
                        {p.status === "terbuka" && bisaApprove && (
                          <button onClick={() => batalPO(p)} data-testid={`inv-po-batal-${p.id}-m`}
                                  className="px-2.5 h-8 rounded hover:bg-rose-50 text-rose-600 text-xs font-medium inline-flex items-center gap-1">
                            <Trash2 className="w-3.5 h-3.5" /> Batal
                          </button>
                        )}
                        <button onClick={() => bukaPreview(p)} data-testid={`inv-po-lihat-${p.id}-m`}
                                className="px-2.5 h-8 rounded hover:bg-slate-100 text-slate-600 text-xs font-medium inline-flex items-center gap-1">
                          <Eye className="w-3.5 h-3.5" /> Lihat
                        </button>
                        <button onClick={() => bukaCetakPO(p)} data-testid={`btn-cetak-po-${p.id}-m`}
                                className="px-2.5 h-8 rounded hover:bg-slate-100 text-slate-600 text-xs font-medium inline-flex items-center gap-1">
                          <Printer className="w-3.5 h-3.5" /> Cetak
                        </button>
                      </KartuBarisAksi>
                    </KartuBaris>
                  );
                })}
              </LoadingState>
            </DaftarKartuMobile>

            {/* Paginasi server-side sungguhan (poin 2) — pola yang sama dengan
                InventoryProducts.jsx: tombol Sebelumnya/Berikutnya + "Halaman
                X dari Y", dipasang SEKALI untuk tabel desktop & kartu mobile
                karena keduanya menampilkan data `pos` yang sama. */}
            {poMeta.total_pages > 1 && (
              <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between text-sm">
                <button disabled={poPage <= 1} onClick={() => setPoPage(p => Math.max(1, p - 1))}
                        data-testid="inv-po-prev"
                        className="px-3 h-8 rounded border border-slate-200 disabled:opacity-40">
                  Sebelumnya
                </button>
                <span className="text-slate-500" data-testid="inv-po-halaman">
                  Halaman {poPage} dari {poMeta.total_pages}
                </span>
                <button disabled={poPage >= poMeta.total_pages}
                        onClick={() => setPoPage(p => Math.min(poMeta.total_pages, p + 1))}
                        data-testid="inv-po-next"
                        className="px-3 h-8 rounded border border-slate-200 disabled:opacity-40">
                  Berikutnya
                </button>
              </div>
            )}
          </>
        ) : tab === "hibah" ? (
          <>
            <div className="px-4 pt-3 pb-1 text-xs text-slate-500">
              Barang tanpa biaya (hibah/sitaan/hadiah/inventaris) butuh persetujuan manager inventory
              ke atas (SPV Finance/CEO/Admin) sebelum stok & jurnal terbentuk — pengaju tidak bisa
              menyetujui pengajuannya sendiri.
            </div>
            <TabelDesktop>
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr className="text-left">
                    <SortTh label="Kode / Tanggal" sortKeyName="tanggal" sortKey={hibahSort.sortKey} sortDir={hibahSort.sortDir} onSort={hibahSort.toggle} testid="inv-hibah-sort-tanggal" />
                    <SortTh label="Sumber" sortKeyName="sumber" sortKey={hibahSort.sortKey} sortDir={hibahSort.sortDir} onSort={hibahSort.toggle} testid="inv-hibah-sort-sumber" />
                    <th className="px-3 py-2.5 font-semibold">Gudang</th>
                    <th className="px-3 py-2.5 font-semibold">Diajukan oleh</th>
                    <SortTh label="Nilai" sortKeyName="nilai_total" sortKey={hibahSort.sortKey} sortDir={hibahSort.sortDir} onSort={hibahSort.toggle} align="right" testid="inv-hibah-sort-nilai" />
                    <th className="px-3 py-2.5 font-semibold">Status</th>
                    <th className="px-3 py-2.5 font-semibold text-right">Aksi</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  <LoadingState loading={loading} error={null} kosong={!loading && hibahPengajuan.length === 0}
                                onRetry={load} varian="tabel" kolom={7} pesanKosong="Belum ada pengajuan barang tanpa biaya"
                                data-testid="inv-hibah-tabel-state">
                    {sortRows(hibahPengajuan, hibahSort.sortKey, hibahSort.sortDir).map(p => {
                      const st = STATUS_HIBAH[p.status] || STATUS_HIBAH.menunggu_approval;
                      return (
                        <tr key={p.id} data-testid={`inv-hibah-row-${p.id}`} className="hover:bg-slate-50/60">
                          <td className="px-3 py-2.5">
                            <p className="font-mono text-[11px] text-slate-500">{p.code}</p>
                            <p className="text-slate-800">{formatTanggal(p.tanggal)}</p>
                          </td>
                          <td className="px-3 py-2.5 text-slate-800 capitalize">{p.sumber}</td>
                          <td className="px-3 py-2.5 text-slate-600">{p.warehouse_nama}</td>
                          <td className="px-3 py-2.5 text-slate-600 text-xs">{p.diajukan_by_name || p.diajukan_by}</td>
                          <td className="px-3 py-2.5 text-right font-mono text-slate-900">{idr(p.nilai_total)}</td>
                          <td className="px-3 py-2.5">
                            <span className={`text-[11px] px-2 py-0.5 rounded border inline-flex items-center gap-1 ${st.c}`}>
                              {p.status === "menunggu_approval" && <ShieldAlert className="w-3 h-3" />}
                              {st.l}
                            </span>
                            {(p.self_approved_note || p.self_rejected_note) && (
                              <p className="text-[10px] text-amber-700 mt-1 max-w-[200px]">Jalur darurat: disetujui/ditolak oleh pengaju sendiri (role istimewa)</p>
                            )}
                          </td>
                          <td className="px-3 py-2.5">
                            <div className="flex items-center justify-end gap-1 flex-wrap">
                              {p.status === "menunggu_approval" && bisaApprove && (
                                <>
                                  <button onClick={() => approveHibah(p)} data-testid={`inv-hibah-approve-${p.id}`}
                                          className="px-2 h-8 rounded bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-medium inline-flex items-center gap-1">
                                    <CheckCircle2 className="w-3.5 h-3.5" /> Setujui
                                  </button>
                                  <button onClick={() => tolakHibah(p)} data-testid={`inv-hibah-tolak-${p.id}`}
                                          className="px-2 h-8 rounded bg-rose-50 hover:bg-rose-100 text-rose-700 text-xs font-medium inline-flex items-center gap-1">
                                    <XCircle className="w-3.5 h-3.5" /> Tolak
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </LoadingState>
                </tbody>
              </table>
            </TabelDesktop>

            <DaftarKartuMobile testid="inv-hibah-kartu">
              <LoadingState loading={loading} error={null} kosong={!loading && hibahPengajuan.length === 0}
                            onRetry={load} varian="kartu" pesanKosong="Belum ada pengajuan barang tanpa biaya"
                            data-testid="inv-hibah-kartu-state">
                {sortRows(hibahPengajuan, hibahSort.sortKey, hibahSort.sortDir).map(p => {
                  const st = STATUS_HIBAH[p.status] || STATUS_HIBAH.menunggu_approval;
                  return (
                    <KartuBaris key={p.id} testid={`inv-hibah-card-${p.id}`}>
                      <KartuBarisHeader
                        kiri={<>
                          <p className="font-mono text-[11px] text-slate-500">{p.code}</p>
                          <p className="text-sm font-semibold text-slate-900 capitalize">{p.sumber}</p>
                          <p className="text-xs text-slate-500 mt-0.5">{formatTanggal(p.tanggal)} · {p.warehouse_nama}</p>
                        </>}
                        kanan={
                          <span className={`text-[11px] px-2 py-0.5 rounded border inline-flex items-center gap-1 ${st.c}`}>
                            {p.status === "menunggu_approval" && <ShieldAlert className="w-3 h-3" />}
                            {st.l}
                          </span>
                        }
                      />
                      <KartuBarisMeta label="Diajukan oleh" value={p.diajukan_by_name || p.diajukan_by} />
                      <KartuBarisMeta label="Nilai" value={idr(p.nilai_total)} tebal />
                      {p.status === "menunggu_approval" && bisaApprove && (
                        <KartuBarisAksi>
                          <button onClick={() => approveHibah(p)} data-testid={`inv-hibah-approve-${p.id}-m`}
                                  className="px-2.5 h-8 rounded bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-medium inline-flex items-center gap-1">
                            <CheckCircle2 className="w-3.5 h-3.5" /> Setujui
                          </button>
                          <button onClick={() => tolakHibah(p)} data-testid={`inv-hibah-tolak-${p.id}-m`}
                                  className="px-2.5 h-8 rounded bg-rose-50 hover:bg-rose-100 text-rose-700 text-xs font-medium inline-flex items-center gap-1">
                            <XCircle className="w-3.5 h-3.5" /> Tolak
                          </button>
                        </KartuBarisAksi>
                      )}
                    </KartuBaris>
                  );
                })}
              </LoadingState>
            </DaftarKartuMobile>
          </>
        ) : tab === "terima" ? (
          <>
            <TabelDesktop>
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr className="text-left">
                    <SortTh label="Kode / Tanggal" sortKeyName="tanggal" sortKey={terimaSort.sortKey} sortDir={terimaSort.sortDir} onSort={terimaSort.toggle} testid="inv-do-sort-tanggal" />
                    <th className="px-3 py-2.5 font-semibold">Jenis</th>
                    <SortTh label="Asal" sortKeyName="supplier_nama" sortKey={terimaSort.sortKey} sortDir={terimaSort.sortDir} onSort={terimaSort.toggle} testid="inv-do-sort-asal" />
                    <th className="px-3 py-2.5 font-semibold">Gudang</th>
                    <SortTh label="Qty" sortKeyName="qty_total" sortKey={terimaSort.sortKey} sortDir={terimaSort.sortDir} onSort={terimaSort.toggle} align="right" testid="inv-do-sort-qty" />
                    <SortTh label="Nilai" sortKeyName="nilai_total" sortKey={terimaSort.sortKey} sortDir={terimaSort.sortDir} onSort={terimaSort.toggle} align="right" testid="inv-do-sort-nilai" />
                    <th className="px-3 py-2.5 font-semibold">Bayar</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  <LoadingState loading={loading} error={null} kosong={!loading && terima.length === 0}
                                onRetry={load} varian="tabel" kolom={7} pesanKosong="Belum ada penerimaan"
                                data-testid="inv-do-tabel-state">
                    {sortRows(terima, terimaSort.sortKey, terimaSort.sortDir).map(t => (
                      <tr key={t.id} data-testid={`inv-do-row-${t.id}`} className="hover:bg-slate-50/60">
                        <td className="px-3 py-2.5">
                          <p className="font-mono text-[11px] text-slate-500">{t.code}</p>
                          <p className="text-slate-800">{formatTanggal(t.tanggal)}</p>
                        </td>
                        <td className="px-3 py-2.5">
                          <span className={`text-[11px] px-2 py-0.5 rounded border ${t.jenis === "pembelian" ? "bg-sky-50 text-sky-700 border-sky-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                            {t.jenis === "pembelian" ? "Pembelian" : `Tanpa Biaya · ${t.sumber || ""}`}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-slate-800">
                          {t.supplier_nama}
                          {t.po_code && <span className="block text-[11px] text-slate-500">{t.po_code}</span>}
                        </td>
                        <td className="px-3 py-2.5 text-slate-600">{t.warehouse_nama}</td>
                        <td className="px-3 py-2.5 text-right text-slate-800">{t.qty_total}</td>
                        <td className="px-3 py-2.5 text-right font-mono text-slate-900">{idr(t.nilai_total)}</td>
                        <td className="px-3 py-2.5 text-[11px] text-slate-600" data-testid={`inv-do-bayar-${t.id}`}>{labelCaraBayar(t.metode_bayar)}</td>
                      </tr>
                    ))}
                  </LoadingState>
                </tbody>
              </table>
            </TabelDesktop>

            <DaftarKartuMobile testid="inv-do-kartu">
              <LoadingState loading={loading} error={null} kosong={!loading && terima.length === 0}
                            onRetry={load} varian="kartu" pesanKosong="Belum ada penerimaan"
                            data-testid="inv-do-kartu-state">
                {sortRows(terima, terimaSort.sortKey, terimaSort.sortDir).map(t => (
                  <KartuBaris key={t.id} testid={`inv-do-card-${t.id}`}>
                    <KartuBarisHeader
                      kiri={<>
                        <p className="font-mono text-[11px] text-slate-500">{t.code}</p>
                        <p className="text-sm font-semibold text-slate-900">{t.supplier_nama}</p>
                        {t.po_code && <p className="text-[11px] text-slate-500">{t.po_code}</p>}
                        <p className="text-xs text-slate-500 mt-0.5">{formatTanggal(t.tanggal)} · {t.warehouse_nama}</p>
                      </>}
                      kanan={
                        <span className={`text-[11px] px-2 py-0.5 rounded border ${t.jenis === "pembelian" ? "bg-sky-50 text-sky-700 border-sky-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                          {t.jenis === "pembelian" ? "Pembelian" : `Tanpa Biaya · ${t.sumber || ""}`}
                        </span>
                      }
                    />
                    <KartuBarisMeta label="Qty" value={t.qty_total} />
                    <KartuBarisMeta label="Bayar" value={labelCaraBayar(t.metode_bayar)} />
                    <KartuBarisMeta label="Nilai" value={idr(t.nilai_total)} tebal />
                  </KartuBaris>
                ))}
              </LoadingState>
            </DaftarKartuMobile>
          </>
        ) : tab === "tagihan" ? (
          <>
            <TabelDesktop>
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr className="text-left">
                    <SortTh label="Kode / Tanggal" sortKeyName="tanggal" sortKey={tagihanSort.sortKey} sortDir={tagihanSort.sortDir} onSort={tagihanSort.toggle} testid="inv-tagihan-sort-tanggal" />
                    <SortTh label="Supplier" sortKeyName="supplier_nama" sortKey={tagihanSort.sortKey} sortDir={tagihanSort.sortDir} onSort={tagihanSort.toggle} testid="inv-tagihan-sort-supplier" />
                    <th className="px-3 py-2.5 font-semibold">Keterangan</th>
                    <SortTh label="Nominal" sortKeyName="nominal" sortKey={tagihanSort.sortKey} sortDir={tagihanSort.sortDir} onSort={tagihanSort.toggle} align="right" testid="inv-tagihan-sort-nominal" />
                    <SortTh label="Sisa" sortKeyName="sisa" sortKey={tagihanSort.sortKey} sortDir={tagihanSort.sortDir} onSort={tagihanSort.toggle} align="right" testid="inv-tagihan-sort-sisa" />
                    <th className="px-3 py-2.5 font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  <LoadingState loading={loading} error={null} kosong={!loading && (tagihan.items || []).length === 0}
                                onRetry={load} varian="tabel" kolom={6} pesanKosong="Belum ada tagihan pembelian"
                                data-testid="inv-tagihan-tabel-state">
                    {sortRows(tagihan.items || [], tagihanSort.sortKey, tagihanSort.sortDir).map(u => (
                      <tr key={u.id} data-testid={`inv-tagihan-${u.id}`} className="hover:bg-slate-50/60">
                        <td className="px-3 py-2.5">
                          <p className="font-mono text-[11px] text-slate-500">{u.code}</p>
                          <p className="text-slate-800">{formatTanggal(u.tanggal)}</p>
                        </td>
                        <td className="px-3 py-2.5 text-slate-800">{u.supplier_nama}</td>
                        <td className="px-3 py-2.5 text-slate-600 text-xs">{u.keterangan}</td>
                        <td className="px-3 py-2.5 text-right font-mono">{idr(u.nominal)}</td>
                        <td className="px-3 py-2.5 text-right font-mono text-rose-700">{idr(u.sisa)}</td>
                        <td className="px-3 py-2.5">
                          <span className={`text-[11px] px-2 py-0.5 rounded border ${u.status === "lunas" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                            {u.status === "lunas" ? <><CheckCircle2 className="w-3 h-3 inline" /> Lunas</> : "Belum dibayar"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </LoadingState>
                </tbody>
              </table>
            </TabelDesktop>

            <DaftarKartuMobile testid="inv-tagihan-kartu">
              <LoadingState loading={loading} error={null} kosong={!loading && (tagihan.items || []).length === 0}
                            onRetry={load} varian="kartu" pesanKosong="Belum ada tagihan pembelian"
                            data-testid="inv-tagihan-kartu-state">
                {sortRows(tagihan.items || [], tagihanSort.sortKey, tagihanSort.sortDir).map(u => (
                  <KartuBaris key={u.id} testid={`inv-tagihan-card-${u.id}`}>
                    <KartuBarisHeader
                      kiri={<>
                        <p className="font-mono text-[11px] text-slate-500">{u.code}</p>
                        <p className="text-sm font-semibold text-slate-900">{u.supplier_nama}</p>
                        <p className="text-xs text-slate-500 mt-0.5">{formatTanggal(u.tanggal)}</p>
                      </>}
                      kanan={
                        <span className={`text-[11px] px-2 py-0.5 rounded border ${u.status === "lunas" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                          {u.status === "lunas" ? "Lunas" : "Belum dibayar"}
                        </span>
                      }
                    />
                    <KartuBarisMeta label="Keterangan" value={u.keterangan} />
                    <KartuBarisMeta label="Nominal" value={idr(u.nominal)} />
                    <KartuBarisMeta label="Sisa" value={idr(u.sisa)} tebal />
                  </KartuBaris>
                ))}
              </LoadingState>
            </DaftarKartuMobile>
          </>
        ) : tab === "stokmenipis" ? (
          <StokMenipisPanel gudang={gudang} suppliers={suppliers} setSuppliers={setSuppliers}
                            canCreate={can("inventory_po", "create") && bolehGudang}
                            onDrafPO={() => {
                              // §35 (26 Agu 2026): tombol ini kini menghasilkan DRAF,
                              // bukan PO sungguhan — jadi arahkan ke tab "Draf PO",
                              // bukan ke daftar PO. Sebelumnya staf dilempar ke tab PO
                              // dan melihat dokumen bernomor sudah jadi, padahal
                              // tombolnya bertuliskan "draf".
                              setTab("draf"); loadDraf();
                            }} />
        ) : (
          // Poin 14c — tab Draf PO: SELALU terpisah dari daftar PO biasa
          // (lihat komentar besar di `purchase.py::buat_draf_po`).
          <DrafPOPanel drafList={drafList} loading={drafLoading}
                      canCreate={can("inventory_po", "create") && bolehGudang}
                      canHapus={can("inventory_po", "delete")}
                      onLanjutkan={(d) => {
                        setFormDraftId(d.id);
                        setForm({
                          tanggal: d.tanggal || hariIniISO(),
                          supplier_id: d.supplier_id || "", supplier_nama: d.supplier_nama || "",
                          warehouse_id: d.warehouse_id || gudang[0]?.id || "",
                          skema: d.skema === "second" ? "second" : "new",
                          // Draf tidak menyimpan cara bayar lagi (backend
                          // `_nilai_bayar_draf`); penanda trade-in ikut dipulihkan.
                          barang_tradein: !!d.barang_tradein,
                          tanggal_jatuh_tempo: d.tanggal_jatuh_tempo || "",
                          no_po_supplier: d.no_po_supplier_draf || "",
                          catatan: d.catatan || "", kena_ppn: !!d.kena_ppn,
                          catatan_unit: (d.items || []).some(it => (it.units || []).some(u => u.catatan)),
                          items: (d.items && d.items.length > 0 ? d.items : [{ sku: "", qty: 1, harga_per_unit: "", units: [] }])
                            .map(it => ({
                              sku: it.sku || "", qty: it.qty || 0, harga_per_unit: it.harga_per_unit || "",
                              units: it.units || [], satuan_input: it.satuan_input || "kecil",
                            })),
                        });
                      }}
                      onHapus={async (d) => {
                        if (!window.confirm(`Hapus draf PO ini? Tindakan ini tidak bisa dibatalkan.`)) return;
                        try {
                          await api.delete(`/inventory/po/draf/${d.id}`);
                          toast.success("Draf PO dihapus");
                          loadDraf();
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || "Gagal menghapus draf");
                        }
                      }} />
        )}
        {tab === "tagihan" && (
          <div className="px-4 py-3 border-t border-slate-100 text-xs text-slate-500">
            Pelunasan tagihan dilakukan tim Finance di <strong>Finance › Pelunasan Pembelian</strong> (satu buku
            dengan Finance › Utang) — di sana saldo COA Kas/Bank otomatis berkurang.
          </div>
        )}
      </div>

      {form && <FormPO form={form} setForm={setForm} gudang={gudang} suppliers={suppliers} setSuppliers={setSuppliers}
                       busy={busy} draftId={formDraftId}
                       onSimpanDraf={async () => {
                         // Poin 14b/14c — "Simpan sebagai Draf" dari palang
                         // UnsavedGuardBar. Draf yang SUDAH ada (lagi
                         // dilanjutkan) di-UPDATE (PUT), draf yang benar-benar
                         // baru di-CREATE (POST) — keduanya validasi LONGGAR
                         // (lihat `bersihkanDraf`/backend `buat_draf_po`),
                         // TIDAK lewat `kirim()` (yang mewajibkan pesan sukses
                         // generik "PO dibuat" yang salah konteks di sini).
                         setBusy(true);
                         try {
                           const payload = bersihkanDraf(form);
                           if (formDraftId) {
                             await api.put(`/inventory/po/draf/${formDraftId}`, payload);
                             toast.success("Draf PO diperbarui");
                           } else {
                             await api.post("/inventory/po/draf", payload);
                             toast.success("Disimpan sebagai draf PO — belum jadi PO sungguhan");
                           }
                           setForm(null);
                           setFormDraftId(null);
                           await loadDraf();
                         } catch (e) {
                           toast.error(e?.response?.data?.detail || "Gagal menyimpan draf PO");
                         } finally {
                           setBusy(false);
                         }
                       }}
                       onSimpan={async () => {
                         if (!form.supplier_id) {
                           toast.error("Pilih supplier dari daftar dulu (atau tambah cepat) — supplier bebas ketik tidak diterima lagi");
                           return;
                         }
                         // Poin 14c — melanjutkan draf: PROMOSIKAN (nomor PO
                         // baru terbit di sini, bukan sebelumnya), bukan bikin
                         // PO lepas dari nol.
                         const ok = formDraftId
                           ? await kirim(() => api.post(`/inventory/po/draf/${formDraftId}/promosikan`, bersihkanPO(form)),
                                        "Draf dipromosikan jadi PO")
                           : await kirim(() => api.post("/inventory/po", bersihkanPO(form)), "PO dibuat");
                         if (ok) { setForm(null); setFormDraftId(null); loadDraf(); }
                       }} />}

      {/* Poin 12 — View: pratinjau PO sebelum edit/batal. */}
      {(previewPO || previewLoading) && (
        <ModalPreviewPO po={previewPO} loading={previewLoading} onClose={() => setPreviewPO(null)}
                        bisaEdit={bisaEditPO} bisaBatal={bisaApprove}
                        onEdit={bukaEditPO} onBatal={(p) => { setPreviewPO(null); batalPO(p); }}
                        onCetak={(p) => { setPreviewPO(null); bukaCetakPO(p); }} />
      )}

      {/* Poin 12 — Edit: form full-replace, kontrak SAMA dengan Create tapi
          mengunci qty/harga baris yang sudah ada penerimaannya (PUT /po/{id}
          menegakkan ulang semuanya di backend — ini murni supaya staf tidak
          bolak-balik toast 400). */}
      {editPO && <FormEditPO editPO={editPO} setEditPO={setEditPO} gudang={gudang} suppliers={suppliers}
                             setSuppliers={setSuppliers} busy={busy} onSimpan={simpanEditPO} />}

      {hibah && <FormHibah form={hibah} setForm={setHibah} gudang={gudang} busy={busy}
                           onSimpan={async () => {
                             // Poin 5 (brief owner 14 Agu): SEKARANG cuma pengajuan — stok &
                             // jurnal belum terbentuk sampai manager inventory ke atas
                             // menyetujuinya di tab "Tanpa Biaya". Pindah ke tab itu supaya
                             // pengajuan yang baru dibuat langsung kelihatan.
                             const ok = await kirim(
                               () => api.post("/inventory/penerimaan-non-pembelian", bersihkanHibah(hibah)),
                               "Pengajuan tersimpan — menunggu approval");
                             if (ok) { setHibah(null); setTab("hibah"); }
                           }} />}

      {terimaFor && <FormTerima po={terimaFor} onClose={() => setTerimaFor(null)} busy={busy}
                                onSimpan={async (body) => {
                                  const ok = await kirim(
                                    () => api.post(`/inventory/po/${terimaFor.id}/terima`, body),
                                    "Barang diterima");
                                  if (ok) setTerimaFor(null);
                                }} />}

      {/* Cetak Faktur Pembelian (brief #18) */}
      {cetakPO && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto"
          onClick={() => setCetakPO(null)}>
          <div className="bg-white rounded-2xl w-full max-w-3xl my-8 shadow-2xl" onClick={e => e.stopPropagation()}
            data-testid="cetak-po-modal">
            <div className="sticky top-0 bg-slate-900 text-white px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
              <h3 className="font-display font-semibold inline-flex items-center gap-2">
                <Printer className="w-4 h-4" /> Cetak Faktur Pembelian
              </h3>
              <button onClick={() => setCetakPO(null)} className="p-2 hover:bg-white/20 rounded-lg">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-6">
              {cetakPO.step === "pilih_template" ? (
                <div className="space-y-4">
                  <PemilihTemplate templates={cetakPO.templates} value={cetakPO.templateId}
                    onChange={(id) => setCetakPO(m => ({ ...m, templateId: id }))} jenisLabel="faktur pembelian"
                    testidPrefix="po-cetak-tpl" />
                  <div className="flex justify-end">
                    <button onClick={() => muatDokumenPO(cetakPO.po, cetakPO.templates.find(t => t.id === cetakPO.templateId))}
                      data-testid="po-cetak-tpl-lanjut"
                      className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium">
                      Lanjutkan
                    </button>
                  </div>
                </div>
              ) : cetakPO.step === "kosong" ? (
                <p className="text-sm text-slate-500 text-center py-8" data-testid="po-cetak-tpl-kosong">
                  Belum ada template faktur pembelian. Buat dulu di menu Template Faktur &amp; Struk.
                </p>
              ) : cetakPO.loading || !cetakPO.data ? (
                <p className="text-center text-sm text-slate-500 py-12">Memuat dokumen...</p>
              ) : (
                <FakturPrint data={cetakPO.data} />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const inp = "w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200";
// Kotak isian BERLEBAR TETAP (kolom Serial Number per unit): `inp` membawa
// `w-full`, dan di CSS hasil build `.w-full` tertulis SESUDAH `.w-44`/`.w-48`,
// jadi `${inp} w-48` tetap selebar baris — kotak ber-`shrink-0` lalu mendorong
// kotak IMEI di sebelahnya sampai gepeng. Lebarnya ditambahkan pemanggil.
const inpTetap = "h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200";

// Input rupiah berformat (poin 11 — pola yang sama dengan InventoryProducts.jsx):
// tampil "12.500", tersimpan "12500", supaya nol berlebih langsung kelihatan.
function formatRibuan(v) {
  const d = String(v ?? "").replace(/\D/g, "");
  return d ? Number(d).toLocaleString("id-ID") : "";
}

function InputRupiah({ value, onChange, testid, placeholder, ariaLabel, disabled = false }) {
  return (
    <div className="relative">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-slate-400 pointer-events-none">Rp</span>
      <input inputMode="numeric" value={formatRibuan(value)} placeholder={placeholder || "0"}
             onChange={e => onChange(e.target.value.replace(/\D/g, ""))} disabled={disabled}
             data-testid={testid} aria-label={ariaLabel}
             className={`${inp} pl-9 text-right font-mono ${disabled ? "bg-slate-50 text-slate-500" : ""}`} />
    </div>
  );
}

function bersihkanPO(f) {
  const tradein = !!f.barang_tradein;
  return {
    ...f,
    catatan_unit: undefined,   // saklar UI saja (P9), bukan field PO
    // Brief 22 Sep 2026 (H1): cara bayar & akun sumber TIDAK dikirim lagi —
    // PO baru selalu tagihan Finance; backend menolak kas/bank. `undefined`
    // dibuang JSON.stringify, jadi form lama yang masih menyimpan nilai itu di
    // state (mis. draf yang dibuka ulang) tidak ikut mengirimnya.
    metode_bayar: undefined,
    sumber_akun_id: undefined,
    barang_tradein: tradein,
    // PO Barang Trade-in tidak punya tagihan, jadi tidak punya jatuh tempo.
    tanggal_jatuh_tempo: tradein ? null : (f.tanggal_jatuh_tempo || null),
    // PPN (keputusan owner 18 Agu 2026) — murni informasi di PO (PO tetap nol
    // jurnal), pra-isi bawaan untuk pop-up konfirmasi di form Terima. Trade-in
    // dari pelanggan tidak punya faktur pajak (backend menolaknya).
    kena_ppn: tradein ? false : !!f.kena_ppn,
    items: f.items.filter(i => i.sku).map(i => ({
      sku: i.sku,
      qty: Number(i.qty) || 0,
      harga_per_unit: Number(i.harga_per_unit) || 0,
      units: (i.units || []).filter(u => u.identifier).map(u => ({
        identifier: u.identifier, harga: Number(u.harga) || Number(i.harga_per_unit) || 0,
        // P9 (brief 4 Sep 2026): catatan kondisi per unit second — ikut ke
        // `UnitInput.catatan` dan mendarat di `inventory_units.catatan`.
        catatan: (u.catatan || "").trim(),
      })),
      satuan_input: i.satuan_input || "kecil",
    })),
  };
}

// Poin 14c — bentuk body untuk `POST/PUT /inventory/po/draf` (`DraftPOReq`,
// backend). SENGAJA BEDA dari `bersihkanPO` di atas dalam DUA hal:
//   1. Baris kosong (SKU belum diisi) TIDAK dibuang — draf adalah coretan
//      tengah jalan, baris yang baru mulai diisi harus tetap tersimpan
//      supaya staf bisa melanjutkannya nanti persis dari titik berhenti.
//   2. `no_po_supplier` -> `no_po_supplier_draf` (field TERPISAH, bukan yang
//      dipantau index unik `uniq_po_supplier_no`) — lihat blok komentar
//      besar di `purchase.py::buat_draf_po` soal kenapa ini wajib.
function bersihkanDraf(f) {
  return {
    tanggal: f.tanggal || "",
    supplier_id: f.supplier_id || null, supplier_nama: f.supplier_nama || "",
    warehouse_id: f.warehouse_id || null, skema: f.skema || "new",
    // Draf tidak menyimpan cara bayar (brief 22 Sep 2026, H1 — backend
    // `_nilai_bayar_draf`); penanda trade-in DIKIRIM SETIAP simpan karena
    // PUT draf yang tidak membawanya menyimpan `false`.
    barang_tradein: !!f.barang_tradein,
    tanggal_jatuh_tempo: f.tanggal_jatuh_tempo || null, cabang: f.cabang || null,
    no_po_supplier_draf: f.no_po_supplier || "",
    catatan: f.catatan || "", kena_ppn: !!f.kena_ppn,
    items: (f.items || []).map(i => ({
      sku: i.sku || "",
      qty: Number(i.qty) || 0,
      harga_per_unit: Number(i.harga_per_unit) || 0,
      units: (i.units || []).map(u => ({
        identifier: u.identifier || "", harga: Number(u.harga) || 0,
        catatan: (u.catatan || "").trim(),
      })),
      satuan_input: i.satuan_input || "kecil",
    })),
  };
}

// Poin 12 — Edit: bentuk body PERSIS sama dengan POReq (backend `edit_po`
// menerima body yang SAMA dengan create_po, full-replace). Field UI-only
// (`id`, `code`, `status`, `qty_diterima_total`, `items[].qty_diterima`)
// DIBUANG di sini, bukan dikirim ke backend — `ItemInput` tidak mengenalnya
// dan backend membaca qty_diterima lama dari dokumen PO sendiri, bukan dari
// input klien (kalau tidak, klien bisa "menaikkan" qty_diterima seenaknya).
function bersihkanEditPO(f) {
  return {
    tanggal: f.tanggal,
    supplier_id: f.supplier_id, supplier_nama: f.supplier_nama,
    // `metode_bayar`, `sumber_akun_id`, dan `barang_tradein` SENGAJA tidak
    // dikirim (brief 22 Sep 2026, H1): backend `edit_po` menguncinya ke nilai
    // tersimpan dan menolak 400 kalau medan itu DIKIRIM dengan nilai lain.
    // Tidak dikirim = PO lama kas/bank/trade-in tetap seperti dulu.
    warehouse_id: f.warehouse_id, skema: f.skema,
    tanggal_jatuh_tempo: f.tanggal_jatuh_tempo || null,
    cabang: f.cabang || null,
    no_po_supplier: f.no_po_supplier || "",
    catatan: f.catatan || "",
    kena_ppn: !!f.kena_ppn,
    items: f.items.filter(i => i.sku).map(i => ({
      sku: i.sku,
      qty: Number(i.qty) || 0,
      harga_per_unit: Number(i.harga_per_unit) || 0,
      units: (i.units || []).filter(u => u.identifier).map(u => ({
        identifier: u.identifier, harga: Number(u.harga) || Number(i.harga_per_unit) || 0,
        // P9 (brief 4 Sep 2026): catatan kondisi per unit second — ikut ke
        // `UnitInput.catatan` dan mendarat di `inventory_units.catatan`.
        catatan: (u.catatan || "").trim(),
      })),
      satuan_input: i.satuan_input || "kecil",
    })),
  };
}

function bersihkanHibah(f) {
  return {
    ...f,
    items: f.items.filter(i => i.sku).map(i => ({
      sku: i.sku,
      qty: Number(i.qty) || 0,
      harga_per_unit: Number(i.harga_per_unit) || 0,
      units: (i.units || []).filter(u => u.identifier).map(u => ({
        identifier: u.identifier, harga: Number(u.harga) || Number(i.harga_per_unit) || 0,
        // P9 (brief 4 Sep 2026): catatan kondisi per unit second — ikut ke
        // `UnitInput.catatan` dan mendarat di `inventory_units.catatan`.
        catatan: (u.catatan || "").trim(),
        // Serial Number kedua (brief 22 Sep 2026, A9) — hanya dikirim bila
        // diisi; kosong = unit tanpa SN, sama persis dengan perilaku lama.
        ...((u.serial_number || "").trim() ? { serial_number: u.serial_number.trim() } : {}),
      })),
      satuan_input: i.satuan_input || "kecil",
    })),
  };
}

function Kartu({ label, nilai, sub, tone }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
      <p className={`font-display text-xl font-bold mt-1.5 ${tone || "text-slate-900"}`}>{nilai}</p>
      <p className="text-[11px] text-slate-500 mt-0.5">{sub}</p>
    </div>
  );
}

// ============ Supplier — dropdown wajib dari master + tambah cepat ============
// Dulu form ini jatuh ke <input> bebas kalau daftar supplier kosong — ketikan
// "PT Maju", "PT. Maju", "pt maju" jadi tiga supplier berbeda di laporan.
// Sekarang SELALU dropdown dari master (mengirim supplier_id, bukan cuma
// nama); kalau supplier belum ada, staf memakai "Tambah cepat" (POST
// /supplier/cepat, idempoten by nama) tanpa keluar dari form PO / draf PO.
//
// `onSupplierBaru` menerima OBJEK supplier lengkap (bukan cuma id) supaya
// pemanggil bisa langsung memilihnya tanpa mencari-cari lagi di array
// `suppliers` miliknya sendiri — array itu baru terisi supplier baru SETELAH
// re-render berikutnya (state React belum ter-flush saat callback ini masih
// berjalan), jadi `suppliers.find(...)` di titik ini pasti belum ketemu.
function SupplierPicker({ suppliers, value, onPilih, onSupplierBaru, testid, disabled = false }) {
  const { can } = useAuth();
  const bolehTambah = can("inventory_po", "create") && !disabled;
  const [tambah, setTambah] = useState(false);
  const [nama, setNama] = useState("");
  const [telepon, setTelepon] = useState("");
  const [busy, setBusy] = useState(false);

  const simpanCepat = async () => {
    if (!nama.trim()) { toast.error("Isi nama supplier dulu"); return; }
    setBusy(true);
    try {
      const r = await api.post("/supplier/cepat", { nama: nama.trim(), telepon: telepon.trim() });
      onSupplierBaru(r.data.supplier);
      toast.success(r.data.message);
      setTambah(false); setNama(""); setTelepon("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menambah supplier");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2">
        <select value={value || ""} data-testid={testid} disabled={disabled}
                onChange={e => onPilih(e.target.value)}
                className={`${inp} flex-1 ${disabled ? "bg-slate-50 text-slate-500" : ""}`}>
          <option value="">— pilih supplier —</option>
          {suppliers.map(s => <option key={s.id} value={s.id}>{s.nama}</option>)}
        </select>
        {bolehTambah && (
          <button type="button" onClick={() => setTambah(t => !t)} data-testid={`${testid}-tambah-cepat-btn`}
                  className="h-10 px-3 rounded-lg border border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-xs font-medium inline-flex items-center gap-1 shrink-0 whitespace-nowrap">
            <Plus className="w-3.5 h-3.5" /> Tambah cepat
          </button>
        )}
      </div>
      {tambah && (
        <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg p-2 flex-wrap">
          <input value={nama} onChange={e => setNama(e.target.value)} placeholder="Nama supplier baru"
                 data-testid={`${testid}-tambah-cepat-nama`} className={`${inp} h-9 flex-1 min-w-[140px]`} />
          <input value={telepon} onChange={e => setTelepon(e.target.value)} placeholder="Telepon (opsional)"
                 data-testid={`${testid}-tambah-cepat-telepon`} className={`${inp} h-9 w-36`} />
          <button type="button" onClick={simpanCepat} disabled={busy} data-testid={`${testid}-tambah-cepat-simpan`}
                  className="h-9 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium disabled:opacity-50 shrink-0">
            {busy ? "Menyimpan..." : "Simpan"}
          </button>
        </div>
      )}
      {suppliers.length === 0 && !tambah && (
        <p className="text-[10px] text-amber-700">Belum ada supplier terdaftar — pakai &quot;Tambah cepat&quot; di atas.</p>
      )}
    </div>
  );
}

function tambahSupplierKeDaftar(setSuppliers, sup) {
  setSuppliers(list => (list.some(s => s.id === sup.id) ? list : [...list, sup].sort((a, b) => (a.nama || "").localeCompare(b.nama || ""))));
}

// ============ Ringkasan total berjalan (poin 3) ============
function hitungRingkasanPO(items) {
  const valid = (items || []).filter(i => i.sku);
  let totalQty = 0, totalNilai = 0;
  for (const i of valid) {
    if (i.units && i.units.length > 0) {
      const unitTerisi = i.units.filter(u => u.identifier);
      totalQty += unitTerisi.length;
      totalNilai += unitTerisi.reduce((s, u) => s + (Number(u.harga) || Number(i.harga_per_unit) || 0), 0);
    } else {
      const qty = Number(i.qty) || 0;
      totalQty += qty;
      totalNilai += qty * (Number(i.harga_per_unit) || 0);
    }
  }
  return { jumlahBaris: valid.length, totalQty, totalNilai };
}

/** Ringkasan berjalan — brief bos minta ini eksplisit tampil PERSIS di atas
 *  tombol "Simpan PO", berubah live saat staf mengetik qty/harga.
 *  `kenaPPN` (opsional, PPN 18 Agu 2026): kalau dicentang, baris DPP/PPN/Total
 *  ditambahkan di bawah — pratinjau saja, nilai sungguhan dari respons backend. */
function RingkasanPO({ items, kenaPPN }) {
  const { jumlahBaris, totalQty, totalNilai } = hitungRingkasanPO(items);
  const ppnPratinjau = kenaPPN ? Math.round(totalNilai * PPN_PERSEN_PREVIEW / 100) : 0;
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2" data-testid="inv-po-ringkasan">
      <div className="grid grid-cols-3 gap-2">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Jumlah Baris</p>
          <p className="text-sm font-bold text-slate-900 mt-0.5" data-testid="inv-po-ringkasan-baris">{jumlahBaris}</p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Total Qty</p>
          <p className="text-sm font-bold text-slate-900 mt-0.5" data-testid="inv-po-ringkasan-qty">{totalQty}</p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
            {kenaPPN ? "DPP (Nilai Barang)" : "Total Nilai"}
          </p>
          <p className="text-sm font-bold text-emerald-700 mt-0.5 font-mono" data-testid="inv-po-ringkasan-nilai">{idr(totalNilai)}</p>
        </div>
      </div>
      {kenaPPN && (
        <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-200">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">PPN {PPN_PERSEN_PREVIEW}%</p>
            <p className="text-sm font-bold text-amber-700 mt-0.5 font-mono" data-testid="inv-po-ringkasan-ppn">{idr(ppnPratinjau)}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Total (DPP + PPN)</p>
            <p className="text-sm font-bold text-slate-900 mt-0.5 font-mono" data-testid="inv-po-ringkasan-total">{idr(totalNilai + ppnPratinjau)}</p>
          </div>
        </div>
      )}
    </div>
  );
}

/** Paragraf keterangan PPN — dipakai `KonfirmasiPPN` (FormTerima, apa adanya)
 * DAN `KonfirmasiSimpanPO` (FormPO/FormEditPO, digabung dengan ringkasan,
 * lihat komentar di sana) supaya kalimatnya tidak menyimpang antara
 * keduanya. Faktor bersama murni teks — TIDAK mengubah perilaku salah satu. */
function _ppnKeterangan(kenaPPN) {
  return kenaPPN ? (
    <p className="text-sm text-slate-600">
      Anda mencentang <strong>kena PPN {PPN_PERSEN_PREVIEW}%</strong> — PPN Masukan akan ditambahkan
      terpisah di atas harga barang. Pastikan supplier memang menerbitkan faktur pajak untuk dokumen ini.
    </p>
  ) : (
    <p className="text-sm text-slate-600">
      Anda <strong>TIDAK</strong> mencentang PPN — dokumen ini akan tersimpan tanpa PPN sama sekali.
      Pastikan supplier memang tidak mengenakan PPN untuk transaksi ini.
    </p>
  );
}

/** Pop-up konfirmasi PPN (brief owner 18 Agu 2026, dipakai FormTerima —
 * PENERIMAAN barang, BUKAN lagi FormPO/FormEditPO, lihat `KonfirmasiSimpanPO`
 * di bawah untuk itu, poin 14a brief owner 23 Agu 2026):
 * "sebelum selesai input PO/DO akan muncul pop up untuk memperingatkan staff
 * 'Apakah barang ini kena PPN?' pilihan (lanjutkan / kembali)". "Kembali"
 * menutup dialog TANPA mengirim apa pun — form yang sedang diisi tetap utuh. */
function KonfirmasiPPN({ kenaPPN, busy, onKembali, onLanjutkan }) {
  return (
    <div className="fixed inset-0 z-[60] bg-black/50 grid place-items-center p-4" onClick={onKembali}
         data-testid="konfirmasi-ppn-modal">
      <div onClick={e => e.stopPropagation()}
           className="bg-white rounded-2xl max-w-sm w-full shadow-2xl p-5 space-y-3">
        <h3 className="font-display font-bold text-base text-slate-900">Apakah barang ini kena PPN?</h3>
        {_ppnKeterangan(kenaPPN)}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onKembali} disabled={busy} data-testid="konfirmasi-ppn-kembali"
                  className="h-10 px-4 rounded-lg border border-slate-200 text-sm disabled:opacity-50">
            Kembali
          </button>
          <button type="button" onClick={onLanjutkan} disabled={busy} data-testid="konfirmasi-ppn-lanjutkan"
                  className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50">
            {busy ? "Menyimpan..." : "Lanjutkan"}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Poin 14a (brief owner 23 Agu 2026): "Buatkan fitur alert saat ingin
 * simpan PO ... 'Apakah sudah yakin produk terinput dengan teliti?'" —
 * SATU dialog akhir untuk tombol Simpan PO / Simpan Perubahan (FormPO &
 * FormEditPO), BUKAN ditumpuk di atas `KonfirmasiPPN` yang sudah ada.
 * Menumpuk dua pop-up berarti staf menjawab DUA pertanyaan berturut-turut
 * untuk satu tombol — keluhan berikutnya sudah bisa ditebak. Badan dialog
 * ini memuat ringkasan item (`hitungRingkasanPO`, sumber SAMA dengan
 * `RingkasanPO` yang sudah tampil di form, supaya angkanya tidak pernah
 * berbeda) DAN pertanyaan PPN yang sudah ada (`_ppnKeterangan`), staf
 * menjawab SEKALI untuk keduanya.
 *
 * SENGAJA TIDAK dipakai `FormTerima` (penerimaan barang) — brief owner minta
 * verifikasi dulu sebelum menyamakan, dan ringkasan "produk terinput dengan
 * teliti" kurang pas untuk konteks penerimaan (barang sudah dipesan di PO,
 * yang diinput di situ adalah realisasi/selisihnya) — lihat laporan agent. */
function KonfirmasiSimpanPO({ items, kenaPPN, busy, onKembali, onLanjutkan }) {
  const { jumlahBaris, totalQty, totalNilai } = hitungRingkasanPO(items);
  return (
    <div className="fixed inset-0 z-[60] bg-black/50 grid place-items-center p-4" onClick={onKembali}
         data-testid="konfirmasi-simpan-po-modal">
      <div onClick={e => e.stopPropagation()}
           className="bg-white rounded-2xl max-w-sm w-full shadow-2xl p-5 space-y-3">
        <h3 className="font-display font-bold text-base text-slate-900">
          Apakah sudah yakin produk terinput dengan teliti?
        </h3>
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-700 space-y-0.5"
             data-testid="konfirmasi-simpan-po-ringkasan">
          <p>{jumlahBaris} baris barang &middot; {totalQty.toLocaleString("id-ID")} unit</p>
          <p className="font-mono font-semibold text-sm text-slate-900">Total nilai: {idr(totalNilai)}</p>
        </div>
        {_ppnKeterangan(kenaPPN)}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onKembali} disabled={busy} data-testid="konfirmasi-simpan-po-kembali"
                  className="h-10 px-4 rounded-lg border border-slate-200 text-sm disabled:opacity-50">
            Kembali, Periksa Lagi
          </button>
          <button type="button" onClick={onLanjutkan} disabled={busy} data-testid="konfirmasi-simpan-po-lanjutkan"
                  className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50">
            {busy ? "Menyimpan..." : "Ya, Simpan"}
          </button>
        </div>
      </div>
    </div>
  );
}

// Poin 12 — `qtyDiterima`/`hargaTerkunci` HANYA dipakai `FormEditPO` (default
// 0/false menjaga `FormPO` Create berperilaku identik seperti sebelumnya).
// Baris yang sudah punya penerimaan: SKU & harga dikunci (edit_po menolaknya
// di backend — ini murni supaya staf tidak bolak-balik toast 400), qty
// dibatasi minimum lewat atribut `min` HTML (bukan pagar keamanan, cuma UX).
// `tampilSN` (brief 22 Sep 2026, A9): kolom Serial Number KEDUA per unit — hanya
// untuk form yang MEMBUAT unit (Barang Tanpa Biaya). Form PO tidak memakainya:
// SN dicatat saat barang benar-benar diterima, bukan saat dipesan.
function BarisItem({ item, idx, skema, onUbah, onHapus, bisaHapus, qtyDiterima = 0, hargaTerkunci = false, tampilCatatan = false,
                     tampilSN = false }) {
  const [saran, setSaran] = useState([]);
  const [produk, setProduk] = useState(null);

  const cari = async (teks) => {
    onUbah(idx, { sku: teks.toUpperCase() });
    if (teks.length < 2) return setSaran([]);
    try {
      const r = await api.get("/inventory/products/lookup", { params: { q: teks } });
      setSaran(r.data.items || []);
    } catch { setSaran([]); }
  };

  const pilih = (p) => {
    setProduk(p);
    setSaran([]);
    onUbah(idx, {
      sku: p.sku,
      harga_per_unit: p.harga_beli || "",
      units: p.has_imei ? [{ identifier: "", harga: "" }] : [],
      qty: p.has_imei ? 0 : 1,
      // Kembali ke satuan kecil tiap kali produk berganti — pilihan satuan
      // besar produk sebelumnya tidak relevan buat produk baru ini.
      satuan_input: "kecil",
    });
  };

  // Baris ini di-key pakai indeks (lihat pemanggilnya), jadi kalau baris lain
  // dihapus, React memakai ULANG instance BarisItem ini untuk item berbeda —
  // tapi state lokal `produk` (hasil pilih() sebelumnya) ikut nyangkut. Tanpa
  // penjagaan ini, dropdown satuan besar / label / cabang IMEI bisa menampilkan
  // data produk lama yang sudah tidak sesuai dengan item.sku baris saat ini.
  const produkAktif = produk && produk.sku === item.sku ? produk : null;
  // Produk SN-saja: nomor unitnya SUDAH Serial Number — kolom SN kedua disembunyikan
  // (backend menolak SN kedua yang berbeda untuk produk ini).
  const kolomSN = tampilSN && (produkAktif?.identifier_type || "imei") !== "sn";

  // Rasio konversi satuan besar->kecil (brief bos #25 / batch D #2a): hanya
  // relevan untuk barang non-IMEI yang punya satuan_besar terdaftar — unit
  // ber-IMEI selalu dihitung satu per satu.
  const punyaSatuanBesar = !!produkAktif?.satuan_besar && !produkAktif?.has_imei;
  const satuanInput = item.satuan_input || "kecil";
  const isiBesar = Number(produkAktif?.isi_per_satuan_besar) || 0;

  // fokus: baris baru hasil tekan Enter langsung menerima kursor, supaya kasir
  // bisa scan/ketik IMEI beruntun tanpa menyentuh mouse. Flag ini tidak ikut
  // ke API — payload submit hanya memetakan {identifier, harga}.
  const tambahUnit = (fokus = false) => onUbah(idx, {
    units: [...(item.units || []).map(u => ({ ...u, fokus: false })), { identifier: "", harga: "", fokus }],
  });
  const ubahUnit = (i, patch) => onUbah(idx, {
    units: item.units.map((u, j) => j === i ? { ...u, ...patch } : u),
  });
  const hapusUnit = (i) => onUbah(idx, { units: item.units.filter((_, j) => j !== i) });

  return (
    <div className="border border-slate-200 rounded-lg p-3 space-y-2 relative">
      <div className="flex items-start gap-2">
        <div className="flex-1 relative">
          <input value={item.sku} onChange={e => cari(e.target.value)} placeholder="Ketik SKU atau nama produk"
                 disabled={qtyDiterima > 0} data-testid={`inv-item-sku-${idx}`}
                 className={`${inp} ${qtyDiterima > 0 ? "bg-slate-50 text-slate-500" : ""}`} />
          {saran.length > 0 && (
            <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-52 overflow-y-auto">
              {saran.map(p => (
                <button key={p.sku} onClick={() => pilih(p)} data-testid={`inv-saran-${p.sku}`}
                        className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm border-b border-slate-50 last:border-0">
                  <span className="font-mono text-xs text-slate-500">{p.sku}</span> · {p.nama}
                  {p.has_imei && <span className="ml-1 text-[10px] text-emerald-700">IMEI</span>}
                  <span className="block text-[11px] text-slate-400">stok {p.stock_qty}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        {/* Poin 12: baris yang sudah ada penerimaannya tidak boleh dihapus
            (edit_po menolak 400 kalau tetap dikirim) — tombolnya disembunyikan
            langsung, bukan tampil lalu gagal (aturan CLAUDE.md #3). */}
        {bisaHapus && qtyDiterima === 0 && (
          <button onClick={() => onHapus(idx)} data-testid={`inv-item-del-${idx}`}
                  className="w-10 h-10 rounded hover:bg-rose-50 text-rose-600 grid place-items-center shrink-0">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
      {qtyDiterima > 0 && (
        <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1"
           data-testid={`inv-item-diterima-${idx}`}>
          <Lock className="w-3 h-3 inline -mt-0.5 mr-1" />
          Sudah diterima {qtyDiterima} unit — SKU &amp; harga baris ini terkunci, qty tidak bisa kurang dari {qtyDiterima}.
        </p>
      )}

      {produkAktif?.has_imei || (item.units && item.units.length > 0) ? (
        <div className="space-y-1.5">
          <p className="text-[11px] text-slate-500">
            Barang ber-IMEI — sebutkan tiap unit
            {skema === "second" && " beserta harganya masing-masing"}
          </p>
          {(item.units || []).map((u, i) => (
            <div key={i} className="flex items-center gap-2">
              <input value={u.identifier} placeholder="IMEI / SN — Enter = tambah unit"
                     onChange={e => ubahUnit(i, { identifier: e.target.value })}
                     onKeyDown={e => {
                       // Enter = tambah unit (poin 15): alur scan gudang itu
                       // "tembak, Enter, tembak" — tanpa ini tangan harus
                       // bolak-balik ke mouse tiap unit.
                       if (e.key === "Enter") {
                         e.preventDefault();
                         if (u.identifier.trim()) tambahUnit(true);
                       }
                     }}
                     autoFocus={!!u.fokus}
                     data-testid={`inv-unit-${idx}-${i}`} className={`${inp} font-mono text-xs`} />
              <BarcodeScanner onDetected={(v) => ubahUnit(i, { identifier: v })}
                              testId={`inv-scan-unit-${idx}-${i}`} title="Scan IMEI unit" />
              {skema === "second" && (
                <div className="w-40 shrink-0">
                  <InputRupiah value={u.harga} placeholder="Harga unit ini"
                         onChange={v => ubahUnit(i, { harga: v })} disabled={hargaTerkunci}
                         testid={`inv-unit-harga-${idx}-${i}`} />
                </div>
              )}
              {kolomSN && (
                <input value={u.serial_number || ""} placeholder="SN (opsional)"
                       title="Serial Number dari kotak/perangkat — opsional, dipakai Price Protection & Claim MDR"
                       onChange={e => ubahUnit(i, { serial_number: e.target.value })}
                       data-testid={`inv-unit-sn-${idx}-${i}`} className={`${inpTetap} font-mono text-xs w-44 shrink-0`} />
              )}
              {tampilCatatan && (
                <input value={u.catatan || ""} placeholder="Catatan unit (lecet, dus hilang, dsb.)"
                       onChange={e => ubahUnit(i, { catatan: e.target.value })}
                       data-testid={`inv-unit-catatan-${idx}-${i}`} className={`${inp} text-xs w-52 shrink-0`} />
              )}
              {item.units.length > 1 && qtyDiterima === 0 && (
                <button onClick={() => hapusUnit(i)} className="w-9 h-9 rounded hover:bg-rose-50 text-rose-600 grid place-items-center shrink-0">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
          {qtyDiterima === 0 && (
            <button onClick={tambahUnit} data-testid={`inv-unit-add-${idx}`}
                    className="text-xs text-emerald-700 hover:underline">+ Tambah unit</button>
          )}
          {skema !== "second" && (
            <InputRupiah value={item.harga_per_unit} placeholder="Harga per unit"
                   onChange={v => onUbah(idx, { harga_per_unit: v })} disabled={hargaTerkunci}
                   testid={`inv-item-harga-${idx}`} />
          )}
        </div>
      ) : (
        <div className="space-y-1.5">
          {punyaSatuanBesar && (
            <div className="flex items-center gap-2 flex-wrap">
              <select value={satuanInput} data-testid={`po-satuan-${idx}`}
                      onChange={e => onUbah(idx, { satuan_input: e.target.value })}
                      className={`${inp} w-auto h-9 text-xs`}>
                <option value="kecil">Beli per {produkAktif.satuan}</option>
                <option value="besar">Beli per {produkAktif.satuan_besar} (isi {isiBesar})</option>
              </select>
              {satuanInput === "besar" && Number(item.qty) > 0 && isiBesar > 0 && (
                <span className="text-[11px] text-emerald-700">
                  {item.qty} {produkAktif.satuan_besar} = {Number(item.qty) * isiBesar} {produkAktif.satuan}
                </span>
              )}
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <input type="number" value={item.qty} min={qtyDiterima > 0 ? qtyDiterima : undefined}
                   placeholder={satuanInput === "besar" ? `Qty (${produkAktif.satuan_besar})` : "Qty"}
                   onChange={e => onUbah(idx, { qty: e.target.value })}
                   data-testid={`inv-item-qty-${idx}`} className={inp} />
            <InputRupiah value={item.harga_per_unit}
                   placeholder={satuanInput === "besar" ? `Harga per ${produkAktif.satuan_besar}` : "Harga per unit"}
                   onChange={v => onUbah(idx, { harga_per_unit: v })} disabled={hargaTerkunci}
                   testid={`inv-item-harga-${idx}`} />
          </div>
        </div>
      )}
    </div>
  );
}

// `dangerous` opsional (default false) — dipakai HANYA oleh FormPO (baris item +
// IMEI + biaya tambahan, form terpanjang di halaman ini). Pembungkus modal lain
// yang lewat sini (FormHibah, FormTerima) TIDAK menandainya, jadi perilakunya
// persis seperti sebelumnya (requestClose langsung menutup, tanpa konfirmasi) —
// satu sumber (useModalGuard) dipakai lewat prop, bukan disalin ulang di tiap
// form, sama seperti pola `Modal` di documents/parts.jsx.
//
// `onSimpanDraf` (poin 14b/14c, opsional): kalau diisi, tombol ketiga "Simpan
// sebagai Draf" muncul di `UnsavedGuardBar` saat staf mencoba menutup form
// yang sudah ada isian. HANYA dioper oleh `FormPO` (bikin PO baru / lanjutkan
// draf) — `FormEditPO`/`FormTerima`/`FormHibah` TIDAK mengoper prop ini,
// jadi guard bar-nya tetap dua tombol seperti sebelumnya (opt-in per
// pemanggil, lihat komentar `UnsavedGuardBar`).
function Kerangka({ judul, ikon: Ikon, children, onClose, onSimpan, busy, label, testid, dangerous = false,
                    onSimpanDraf }) {
  const guard = useModalGuard({ onClose, onSubmit: onSimpan, busy, dangerous });
  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={guard.requestClose}>
      <div ref={guard.contentRef} onClick={e => e.stopPropagation()} data-testid={testid}
           className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] flex flex-col shadow-2xl">
        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-display font-bold text-lg flex items-center gap-2">
            <Ikon className="w-5 h-5 text-emerald-600" /> {judul}
          </h3>
          <button onClick={guard.requestClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 overflow-y-auto flex-1 space-y-3">{children}</div>
        <div className="p-4 border-t border-slate-100 flex justify-end gap-2">
          <button onClick={guard.requestClose} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Batal</button>
          <button onClick={onSimpan} disabled={busy} data-testid={`${testid}-submit`}
                  className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50">
            {busy ? "Menyimpan..." : label}
          </button>
        </div>
        {guard.confirming && (
          <UnsavedGuardBar onStay={guard.cancelClose} onDiscard={guard.confirmDiscard}
                           onSaveDraft={onSimpanDraf}
                           testid={testid ? `${testid}-guard-bar` : "modal-guard-bar"} />
        )}
      </div>
    </div>
  );
}

// ============ Stok Menipis -> Draf PO (batch D #1c) ============
function StokMenipisPanel({ gudang, suppliers, setSuppliers, canCreate, onDrafPO }) {
  const [warehouseId, setWarehouseId] = useState("");
  const [items, setItems] = useState([]);
  const [asumsi, setAsumsi] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formDraf, setFormDraf] = useState(null);
  const [busy, setBusy] = useState(false);
  // Pengurutan tabel (client-side) — urutan bawaan tetap seperti sebelumnya.
  const stokSort = useSortState(null, "asc");

  const load = async (wid) => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get("/inventory/stok-menipis", { params: { warehouse_id: wid || undefined } });
      setItems(r.data.items || []);
      setAsumsi(r.data.asumsi || "");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Gagal memuat stok menipis";
      toast.error(msg);
      setError(msg);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(warehouseId); }, [warehouseId]); // eslint-disable-line react-hooks/exhaustive-deps

  const bukaDraf = () => setFormDraf({
    warehouse_id: warehouseId || gudang[0]?.id || "",
    supplier_id: "", tanggal: hariIniISO(), tanggal_jatuh_tempo: "",
  });

  const submitDraf = async () => {
    if (!formDraf.warehouse_id) { toast.error("Pilih gudang tujuan dulu"); return; }
    // §35 (26 Agu 2026): jatuh tempo TIDAK lagi diwajibkan di sini. Endpoint ini
    // kini menghasilkan DRAF sungguhan — dan draf memang bervalidasi longgar
    // (lihat `buat_draf_po`): coretan boleh belum lengkap. Aturan "Tempo wajib
    // jatuh tempo" tetap ditegakkan, tapi di titik yang benar yaitu saat barang
    // DITERIMA (P10, backend `terima_barang` — bukan lagi saat draf dijadikan PO).
    // Mewajibkannya di muka memaksa staf menebak tanggal untuk dokumen yang
    // baru berupa coretan.
    setBusy(true);
    try {
      // Kirim supplier_id APA ADANYA kalau staf sudah memilih dari dropdown —
      // backend tidak lagi meresolve ulang lewat nama (pintu belakang nama
      // bebas sudah ditutup, brief bos 17 Agu). Supplier di draf ini memang
      // sengaja opsional; kalau belum dipilih, backend jatuh ke placeholder
      // tunggal "(belum ditentukan)".
      const r = await api.post("/inventory/stok-menipis/draf-po", {
        warehouse_id: formDraf.warehouse_id, supplier_id: formDraf.supplier_id || undefined,
        tanggal: formDraf.tanggal, tanggal_jatuh_tempo: formDraf.tanggal_jatuh_tempo,
      });
      toast.success(r.data.message);
      setFormDraf(null);
      await load(warehouseId);
      onDrafPO();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal membuat draf PO"); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <select value={warehouseId} onChange={e => setWarehouseId(e.target.value)}
                data-testid="stokmenipis-gudang" aria-label="Filter gudang stok menipis"
                className="h-9 px-2 border border-slate-200 rounded-lg text-sm">
          <option value="">Semua gudang (gabungan)</option>
          {gudang.map(g => <option key={g.id} value={g.id}>{g.nama}</option>)}
        </select>
        <p className="text-xs text-slate-500">{items.length} barang menipis</p>
        <div className="flex-1" />
        {canCreate && gudang.length > 0 && (
          <button onClick={bukaDraf} data-testid="stokmenipis-drafpo"
                  className="h-9 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium inline-flex items-center gap-2">
            <ClipboardList className="w-4 h-4" /> Buat Draf PO
          </button>
        )}
      </div>
      {asumsi && <p className="text-[11px] text-slate-500">Asumsi saran qty: {asumsi}</p>}

      <TabelDesktop className="border border-slate-200 rounded-xl">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr className="text-left">
              <SortTh label="SKU" sortKeyName="sku" sortKey={stokSort.sortKey} sortDir={stokSort.sortDir} onSort={stokSort.toggle} testid="stokmenipis-sort-sku" />
              <SortTh label="Nama" sortKeyName="nama" sortKey={stokSort.sortKey} sortDir={stokSort.sortDir} onSort={stokSort.toggle} testid="stokmenipis-sort-nama" />
              <SortTh label="Stok" sortKeyName="stock_qty" sortKey={stokSort.sortKey} sortDir={stokSort.sortDir} onSort={stokSort.toggle} align="right" testid="stokmenipis-sort-stok" />
              <th className="px-3 py-2.5 font-semibold text-right">Min. Stok</th>
              <th className="px-3 py-2.5 font-semibold text-right">Saran Qty Pesan</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            <LoadingState loading={loading} error={error} kosong={!loading && !error && items.length === 0}
                          onRetry={() => load(warehouseId)} varian="tabel" kolom={5}
                          pesanKosong="Tidak ada barang menipis — semua stok masih di atas ambang minimum."
                          data-testid="stokmenipis-tabel-state">
              {sortRows(items, stokSort.sortKey, stokSort.sortDir).map(it => (
                <tr key={it.sku} data-testid={`stokmenipis-row-${it.sku}`} className="hover:bg-slate-50/60">
                  <td className="px-3 py-2.5 font-mono text-xs">{it.sku}</td>
                  <td className="px-3 py-2.5 text-slate-800">{it.nama}</td>
                  <td className="px-3 py-2.5 text-right text-rose-700 font-semibold">{it.stock_qty}</td>
                  <td className="px-3 py-2.5 text-right text-slate-600">{it.min_stock}</td>
                  <td className="px-3 py-2.5 text-right font-mono text-slate-900">{it.saran_qty_pesan}</td>
                </tr>
              ))}
            </LoadingState>
          </tbody>
        </table>
      </TabelDesktop>

      <DaftarKartuMobile testid="stokmenipis-kartu" className="border border-slate-200 rounded-xl overflow-hidden">
        <LoadingState loading={loading} error={error} kosong={!loading && !error && items.length === 0}
                      onRetry={() => load(warehouseId)} varian="kartu"
                      pesanKosong="Tidak ada barang menipis — semua stok masih di atas ambang minimum."
                      data-testid="stokmenipis-kartu-state">
          {sortRows(items, stokSort.sortKey, stokSort.sortDir).map(it => (
            <KartuBaris key={it.sku} testid={`stokmenipis-card-${it.sku}`}>
              <KartuBarisHeader
                kiri={<>
                  <p className="font-mono text-[11px] text-slate-500">{it.sku}</p>
                  <p className="text-sm font-semibold text-slate-900">{it.nama}</p>
                </>}
                kanan={<p className="text-sm font-bold text-rose-700">{it.stock_qty}<span className="block text-[10px] font-normal text-slate-400">stok</span></p>}
              />
              <KartuBarisMeta label="Min. Stok" value={it.min_stock} />
              <KartuBarisMeta label="Saran Qty Pesan" value={it.saran_qty_pesan} tebal />
            </KartuBaris>
          ))}
        </LoadingState>
      </DaftarKartuMobile>

      {formDraf && (
        <Kerangka judul="Buat Draf PO dari Stok Menipis" ikon={ClipboardList} testid="stokmenipis-draf-modal"
                  onClose={() => setFormDraf(null)} onSimpan={submitDraf} busy={busy} label="Buat Draf PO">
          <div className="bg-sky-50 border border-sky-200 rounded-lg p-3 text-xs text-sky-800">
            Draf dibuat untuk seluruh barang menipis di gudang ini dengan qty & harga saran otomatis
            (barang ber-IMEI dilewati — buat PO manual untuk itu). Status masih terbuka, belum ada barang diterima.
          </div>
          <div>
            <label htmlFor="stokmenipis-draf-gudang" className="block text-xs font-medium text-slate-600 mb-1">Gudang tujuan</label>
            <select id="stokmenipis-draf-gudang" value={formDraf.warehouse_id} data-testid="stokmenipis-draf-gudang"
                    onChange={e => setFormDraf(f => ({ ...f, warehouse_id: e.target.value }))} className={inp}>
              <option value="">— pilih gudang —</option>
              {gudang.map(g => <option key={g.id} value={g.id}>{g.nama}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Supplier (opsional)</label>
            <SupplierPicker suppliers={suppliers} value={formDraf.supplier_id}
                            onPilih={id => setFormDraf(f => ({ ...f, supplier_id: id }))}
                            onSupplierBaru={sup => {
                              tambahSupplierKeDaftar(setSuppliers, sup);
                              setFormDraf(f => ({ ...f, supplier_id: sup.id }));
                            }}
                            testid="stokmenipis-draf-supplier" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Tanggal</label>
            <DatePicker value={formDraf.tanggal} data-testid="stokmenipis-draf-tanggal"
                        onChange={v => setFormDraf(f => ({ ...f, tanggal: v }))} />
          </div>
          <div>
            {/* Label "*wajib" lama sudah tidak benar sejak P10 (4 Sep 2026) dan
                §35: tanggal ini tidak diwajibkan di sini maupun saat draf jadi
                PO — kewajibannya di form Terima, titik utang lahir (E17 poin 3). */}
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Jatuh tempo <span className="text-slate-400 font-normal">(opsional — wajib paling lambat saat barang diterima)</span>
            </label>
            <DatePicker value={formDraf.tanggal_jatuh_tempo} data-testid="stokmenipis-draf-jatuhtempo"
                        onChange={v => setFormDraf(f => ({ ...f, tanggal_jatuh_tempo: v }))} />
          </div>
        </Kerangka>
      )}
    </div>
  );
}

// ============ Poin 14c — tab Draf PO (brief owner 23 Agu 2026) ============
// Coretan PO yang BELUM sah — belum konsumsi nomor, belum menyentuh index
// unik nomor PO supplier — SELALU tab/daftar terpisah dari PO biasa, tidak
// pernah dicampur (lihat blok komentar besar di `purchase.py::buat_draf_po`).
//
// CATATAN untuk pembaca yang bingung dua arti "draf" di halaman ini: tombol
// "Draf PO" di tab Stok Menipis (`StokMenipisPanel` di atas, testid
// `stokmenipis-draf-*`) BUKAN fitur ini — endpoint lamanya
// (`buat_draf_po_stok_menipis` di purchase.py) namanya menyesatkan: yang
// dibuat SUDAH PO sungguhan berstatus terbuka, sudah konsumsi nomor, sudah
// bisa langsung diterima barangnya. Sudah dilaporkan ke owner (lihat laporan
// agent) — TIDAK diubah perilakunya di batch ini tanpa persetujuan eksplisit.
function DrafPOPanel({ drafList, loading, canCreate, canHapus, onLanjutkan, onHapus }) {
  return (
    <div className="p-4 space-y-3">
      <div className="bg-sky-50 border border-sky-200 rounded-lg p-3 text-xs text-sky-800" data-testid="draf-po-info">
        Draf adalah coretan PO yang BELUM tersimpan sebagai PO sungguhan — belum punya nomor, belum
        kena aturan approval maupun utang apa pun. Tekan &quot;Lanjutkan&quot; untuk melengkapi lalu
        menyimpannya sebagai PO sungguhan, atau &quot;Hapus&quot; untuk membuangnya.
      </div>
      <TabelDesktop>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr className="text-left">
              <th className="px-3 py-2.5 font-semibold">Tanggal</th>
              <th className="px-3 py-2.5 font-semibold">Supplier</th>
              <th className="px-3 py-2.5 font-semibold">Gudang</th>
              <th className="px-3 py-2.5 font-semibold">Ringkasan</th>
              <th className="px-3 py-2.5 font-semibold">Dibuat oleh</th>
              <th className="px-3 py-2.5 font-semibold text-right">Aksi</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            <LoadingState loading={loading} error={null} kosong={!loading && drafList.length === 0}
                          varian="tabel" kolom={6} pesanKosong="Belum ada draf PO tersimpan"
                          data-testid="draf-po-tabel-state">
              {drafList.map(d => (
                <tr key={d.id} data-testid={`draf-po-row-${d.id}`} className="hover:bg-slate-50/60">
                  <td className="px-3 py-2.5 text-slate-800">{formatTanggal(d.tanggal) || "-"}</td>
                  <td className="px-3 py-2.5 text-slate-800">{d.supplier_nama || "(belum dipilih)"}</td>
                  <td className="px-3 py-2.5 text-slate-600">{d.warehouse_nama || "(belum dipilih)"}</td>
                  <td className="px-3 py-2.5 text-slate-600 text-xs">
                    {(d.items || []).filter(i => i.sku).length} baris &middot; {idr(d.nilai_total || 0)}
                  </td>
                  <td className="px-3 py-2.5 text-slate-600 text-xs">{d.created_by_name || d.created_by || "-"}</td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center justify-end gap-1.5 flex-wrap">
                      {canCreate && (
                        <button onClick={() => onLanjutkan(d)} data-testid={`draf-po-lanjutkan-${d.id}`}
                                className="px-2.5 h-8 rounded bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-medium">
                          Lanjutkan
                        </button>
                      )}
                      {/* §43: backend `hapus_draf_po` menuntut izin inventory_po.delete.
                          Sebelumnya tombol ini tanpa gerbang sama sekali. */}
                      {canHapus && (
                        <button onClick={() => onHapus(d)} data-testid={`draf-po-hapus-${d.id}`}
                                className="px-2.5 h-8 rounded bg-rose-50 hover:bg-rose-100 text-rose-700 text-xs font-medium">
                          Hapus
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </LoadingState>
          </tbody>
        </table>
      </TabelDesktop>

      <DaftarKartuMobile testid="draf-po-kartu">
        <LoadingState loading={loading} error={null} kosong={!loading && drafList.length === 0}
                      varian="kartu" pesanKosong="Belum ada draf PO tersimpan"
                      data-testid="draf-po-kartu-state">
          {drafList.map(d => (
            <KartuBaris key={d.id} testid={`draf-po-card-${d.id}`}>
              <KartuBarisHeader
                kiri={<>
                  <p className="text-sm font-semibold text-slate-900">{d.supplier_nama || "(belum dipilih)"}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{formatTanggal(d.tanggal) || "-"} &middot; {d.warehouse_nama || "(belum dipilih)"}</p>
                </>}
              />
              <KartuBarisMeta label="Ringkasan" value={`${(d.items || []).filter(i => i.sku).length} baris · ${idr(d.nilai_total || 0)}`} />
              <KartuBarisMeta label="Dibuat oleh" value={d.created_by_name || d.created_by || "-"} />
              <KartuBarisAksi>
                {canCreate && (
                  <button onClick={() => onLanjutkan(d)} data-testid={`draf-po-lanjutkan-${d.id}-m`}
                          className="px-2.5 h-8 rounded bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-medium">
                    Lanjutkan
                  </button>
                )}
                {canHapus && (
                  <button onClick={() => onHapus(d)} data-testid={`draf-po-hapus-${d.id}-m`}
                          className="px-2.5 h-8 rounded bg-rose-50 hover:bg-rose-100 text-rose-700 text-xs font-medium">
                    Hapus
                  </button>
                )}
              </KartuBarisAksi>
            </KartuBaris>
          ))}
        </LoadingState>
      </DaftarKartuMobile>
    </div>
  );
}

function FormPO({ form, setForm, gudang, suppliers, setSuppliers, busy, onSimpan, onSimpanDraf, draftId }) {
  // Poin 14a (brief owner 23 Agu 2026) — GABUNGAN konfirmasi PPN + ringkasan
  // ketelitian jadi SATU dialog (`KonfirmasiSimpanPO`), bukan ditumpuk di
  // atas dialog PPN yang sudah ada (lihat komentar panjang di komponen itu).
  // Tombol "Simpan PO" di Kerangka TIDAK langsung menyimpan; ia membuka
  // konfirmasi ini dulu. Form state tidak hilang kalau staf memilih
  // "Kembali" — cuma dialognya yang ditutup.
  const [konfirmasiSimpan, setKonfirmasiSimpan] = useState(false);
  // Dialog konfirmasi terbuka DI ATAS Kerangka (modal `dangerous`) di
  // baliknya — daftarkan sebagai lapisan lebih atas supaya Escape untuk
  // dialog ini tidak ikut memicu palang "ada isian belum disimpan" pada
  // form PO di baliknya (lihat "LAPISAN PALING ATAS YANG MENANG" di
  // useModalGuard.jsx).
  useModalLayer(konfirmasiSimpan);
  const ubah = (idx, patch) => setForm(f => ({
    ...f, items: f.items.map((it, i) => i === idx ? { ...it, ...patch } : it),
  }));
  const tradein = !!form.barang_tradein;
  // Penampung trade-in dicatat PER CABANG dan backend menurunkannya dari cabang
  // gudang tujuan (`_akun_penampung_tradein_cabang`) — ditampilkan supaya staf
  // tahu penampung mana yang akan melunasi PO ini sebelum menyimpan.
  const cabangTujuan = (gudang.find(g => g.id === form.warehouse_id) || {}).cabang || "";
  return (
    <>
    <Kerangka judul={draftId ? "Lanjutkan Draf PO" : "Purchase Order Baru"} ikon={ClipboardList}
              testid="inv-po-modal" dangerous onSimpanDraf={onSimpanDraf}
              onClose={() => setForm(null)} onSimpan={() => {
                // Jatuh tempo TIDAK diwajibkan saat membuat PO (P10/E17 poin 3):
                // termin supplier sering belum final saat memesan. Kewajibannya di
                // form Terima — titik utangnya lahir (backend `terima_barang`).
                setKonfirmasiSimpan(true);
              }} busy={busy} label={draftId ? "Promosikan jadi PO" : "Simpan PO"}>
      <div className="bg-sky-50 border border-sky-200 rounded-lg p-3 text-xs text-sky-800">
        PO belum menyentuh pembukuan. Persediaan dan utang baru tercatat saat barang diterima —
        jadi kalau supplier batal kirim, tidak ada jurnal yang perlu dibatalkan.
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Tanggal PO</label>
          <DatePicker value={form.tanggal} data-testid="inv-po-tanggal"
                      onChange={v => setForm(f => ({ ...f, tanggal: v }))} />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Supplier</label>
          {/* Wajib dari master Supplier (poin 1) — bukan ketikan bebas, supaya
              "PT Maju", "PT. Maju", dan "pt maju" tidak jadi tiga supplier di laporan. */}
          <SupplierPicker
            suppliers={suppliers}
            value={form.supplier_id}
            onPilih={(id) => {
              const sup = suppliers.find(s => s.id === id);
              setForm(f => ({ ...f, supplier_id: id, supplier_nama: sup ? sup.nama : "" }));
            }}
            onSupplierBaru={(sup) => {
              tambahSupplierKeDaftar(setSuppliers, sup);
              setForm(f => ({ ...f, supplier_id: sup.id, supplier_nama: sup.nama }));
            }}
            testid="inv-po-supplier"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">No. PO / referensi supplier</label>
          <input value={form.no_po_supplier} data-testid="inv-po-nopo" placeholder="opsional"
                 onChange={e => setForm(f => ({ ...f, no_po_supplier: e.target.value }))} className={inp} />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Gudang tujuan</label>
          <select value={form.warehouse_id} data-testid="inv-po-gudang"
                  onChange={e => setForm(f => ({ ...f, warehouse_id: e.target.value }))} className={inp}>
            {gudang.map(g => <option key={g.id} value={g.id}>{g.nama}</option>)}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Skema input</label>
          <select value={form.skema} data-testid="inv-po-skema"
                  onChange={e => setForm(f => ({ ...f, skema: e.target.value }))} className={inp}>
            <option value="new">Baru — satu harga untuk semua unit</option>
            <option value="second">Second — harga per unit per IMEI</option>
          </select>
          {/* P9 (brief 4 Sep 2026): kolom catatan per unit hanya untuk skema
              second — kondisi tiap unit bekas memang berbeda-beda. */}
          {form.skema === "second" && (
            <label className="mt-1.5 flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
              <input type="checkbox" checked={!!form.catatan_unit} data-testid="inv-po-catatan-unit"
                     onChange={e => setForm(f => ({ ...f, catatan_unit: e.target.checked }))}
                     className="w-3.5 h-3.5 rounded border-slate-300" />
              Tambahkan catatan per unit (kondisi, kelengkapan)
            </label>
          )}
        </div>
        <div>
          {/* Brief 22 Sep 2026 (H1): dropdown "Cara bayar" DIHAPUS — "Admin
              inventory tidak dapat melunaskan barang yang masuk; pelunasan masuk
              ke ranah tim finance". Dulu pembuat PO memilih kas/bank dan staf
              yang menekan Terima sekaligus mengeluarkan uang. Sekarang PO selalu
              jadi tagihan; backend menolak kas/bank (`_validasi_metode_bayar_po`). */}
          <label className="block text-xs font-medium text-slate-600 mb-1">Pembayaran</label>
          <p className="min-h-10 px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 text-xs text-slate-600"
             data-testid="inv-po-info-bayar">
            {tradein
              ? "Lunas otomatis dari penampung trade-in cabang saat barang diterima"
              : "Tagihan dilunasi tim Finance (Finance › Pelunasan Pembelian)"}
          </p>
        </div>
        {!tradein && (
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Jatuh tempo <span className="text-slate-400 font-normal">(opsional — wajib paling lambat saat barang diterima)</span>
            </label>
            <DatePicker value={form.tanggal_jatuh_tempo} data-testid="inv-po-jatuhtempo"
                        onChange={v => setForm(f => ({ ...f, tanggal_jatuh_tempo: v }))} />
          </div>
        )}
      </div>

      {/* Barang Trade-in (keputusan H1/D11) — penanda, BUKAN cara bayar. Akun
          penampungnya tidak dipilih di sini: backend menurunkannya dari cabang
          gudang tujuan, dan setiap IMEI wajib ada di daftar tunggu trade-in
          cabang itu (`_validasi_po_tradein`) — kalau akunnya bisa dipilih,
          centang ini jadi cara "melunasi" supplier biasa dari penampung. */}
      <label className="flex items-start gap-2 text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-lg p-3 cursor-pointer">
        <input type="checkbox" checked={tradein} data-testid="inv-po-tradein"
               onChange={e => {
                 const v = e.target.checked;
                 // Trade-in tanpa PPN & tanpa jatuh tempo (tidak ada tagihan) —
                 // dikosongkan langsung supaya yang tampil = yang terkirim.
                 setForm(f => ({ ...f, barang_tradein: v, ...(v ? { kena_ppn: false, tanggal_jatuh_tempo: "" } : {}) }));
               }}
               className="w-4 h-4 mt-0.5 rounded border-slate-300" />
        <span>
          <strong>Barang Trade-in</strong> — HP dari pelanggan yang nilainya sudah diparkir di penampung
          trade-in cabang saat nota penjualan/akuisisi. Saat diterima, PO ini lunas dari penampung itu dan
          tidak menjadi tagihan Finance.
          {tradein && (
            <span className="block text-xs mt-1 text-slate-600" data-testid="inv-po-tradein-syarat">
              Syarat: setiap IMEI ada di daftar tunggu trade-in cabang{" "}
              <strong>{cabangTujuan || "—"}</strong>, tanpa PPN, tanpa biaya tambahan.
              {!cabangTujuan && (
                <span className="block text-rose-700 mt-0.5">
                  Gudang tujuan ini tidak tertaut ke satu cabang — pilih gudang cabang tempat HP itu diterima dari pelanggan.
                </span>
              )}
            </span>
          )}
        </span>
      </label>

      {/* PPN (keputusan owner 18 Agu 2026): informasi/pra-isi saja di PO — PO
          tetap nol jurnal (lihat spanduk sky di atas). Nilai yang SUNGGUH
          dijurnal ditentukan lagi saat barang diterima (FormTerima), karena
          satu faktur pajak supplier menutupi satu DO, bukan satu PO. */}
      {!tradein && (
        <label className="flex items-center gap-2 text-sm text-slate-700 bg-amber-50 border border-amber-200 rounded-lg p-3 cursor-pointer">
          <input type="checkbox" checked={!!form.kena_ppn} data-testid="inv-po-kena-ppn"
                 onChange={e => setForm(f => ({ ...f, kena_ppn: e.target.checked }))}
                 className="w-4 h-4 rounded border-slate-300" />
          <span>
            Barang ini <strong>kena PPN {PPN_PERSEN_PREVIEW}%</strong> — harga di atas belum termasuk PPN,
            PPN akan ditambahkan terpisah saat barang diterima.
          </span>
        </label>
      )}

      <div className="space-y-2">
        <p className="text-xs font-medium text-slate-600">Barang yang dipesan</p>
        {form.items.map((it, i) => (
          <BarisItem key={i} item={it} idx={i} skema={form.skema} onUbah={ubah}
                     tampilCatatan={form.skema === "second" && !!form.catatan_unit}
                     bisaHapus={form.items.length > 1}
                     onHapus={(idx) => setForm(f => ({ ...f, items: f.items.filter((_, j) => j !== idx) }))} />
        ))}
        <button onClick={() => setForm(f => ({ ...f, items: [...f.items, { sku: "", qty: 1, harga_per_unit: "", units: [] }] }))}
                data-testid="inv-po-additem" className="text-sm text-emerald-700 hover:underline">
          + Tambah barang
        </button>
      </div>

      {/* Poin 3: ringkasan berjalan — PERSIS di atas tombol Simpan PO (footer Kerangka). */}
      <RingkasanPO items={form.items} kenaPPN={form.kena_ppn} />
    </Kerangka>
    {konfirmasiSimpan && (
      <KonfirmasiSimpanPO items={form.items} kenaPPN={!!form.kena_ppn} busy={busy}
                          onKembali={() => setKonfirmasiSimpan(false)}
                          onLanjutkan={() => { setKonfirmasiSimpan(false); onSimpan(); }} />
    )}
    </>
  );
}

// ============ Poin 12 — Edit PO ============
// Bentuk SAMA dengan FormPO (Create), bedanya: field yang sudah "beku" karena
// sebagian barang sudah diterima (`editPO.qty_diterima_total > 0`) dikunci —
// supplier/gudang/skema TIDAK bisa diganti sama sekali (`edit_po` menolaknya
// 400), baris per-SKU yang qty_diterima-nya > 0 mengunci SKU & harga (lihat
// `BarisItem`). Backend menegakkan ULANG semuanya — penguncian di sini murni
// supaya staf tidak bolak-balik toast 400 (aturan CLAUDE.md #3).
function FormEditPO({ editPO, setEditPO, gudang, suppliers, setSuppliers, busy, onSimpan }) {
  // Poin 14a (brief owner 23 Agu 2026): Edit PO SEBELUMNYA menyimpan
  // langsung tanpa konfirmasi apa pun (celah yang dilaporkan agent
  // sebelumnya) — sekarang disambungkan ke dialog gabungan yang SAMA dengan
  // FormPO (`KonfirmasiSimpanPO`), supaya Simpan & Edit berperilaku sama.
  // TIDAK ada `onSimpanDraf` di sini (lihat komentar `Kerangka`) — PO yang
  // sedang diedit sudah PUNYA nomor, "menyimpan sebagai draf" tidak masuk
  // akal untuk dokumen yang sudah sah.
  const [konfirmasiSimpan, setKonfirmasiSimpan] = useState(false);
  useModalLayer(konfirmasiSimpan);
  const ubah = (idx, patch) => setEditPO(f => ({
    ...f, items: f.items.map((it, i) => i === idx ? { ...it, ...patch } : it),
  }));
  const sudahAdaPenerimaan = (editPO.qty_diterima_total || 0) > 0;
  return (
    <>
    <Kerangka judul={`Edit PO ${editPO.code}`} ikon={Pencil} testid="inv-po-edit-modal" dangerous
              onClose={() => setEditPO(null)} onSimpan={() => {
                // Pre-cek yang sama dengan simpanEditPO (parent) — supaya
                // staf menjawab dialog konfirmasi SETELAH isian minimal valid,
                // bukan sebelum (mengurangi bolak-balik toast error).
                if (!editPO.supplier_id) {
                  toast.error("Pilih supplier dari daftar dulu (atau tambah cepat) — supplier bebas ketik tidak diterima lagi");
                  return;
                }
                // Jatuh tempo tidak diwajibkan di sini (P10/E17 poin 3) — lihat simpanEditPO.
                setKonfirmasiSimpan(true);
              }} busy={busy} label="Simpan Perubahan">
      <div className="bg-sky-50 border border-sky-200 rounded-lg p-3 text-xs text-sky-800">
        Mengedit dokumen yang sudah ada — bukan membuat PO baru. Kalau nilainya naik melewati
        ambang approval, status kembali "Menunggu Approval" dan harus disetujui ulang.
      </div>
      {sudahAdaPenerimaan && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 flex items-start gap-2">
          <Lock className="w-4 h-4 mt-0.5 shrink-0" />
          PO ini sudah menerima {editPO.qty_diterima_total} unit — supplier, gudang tujuan, dan skema
          input tidak bisa diubah lagi. Baris yang sudah diterima terkunci SKU &amp; harganya (lihat tanda
          kunci per baris); qty boleh dinaikkan, tidak boleh diturunkan di bawah yang sudah diterima.
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Tanggal PO</label>
          <DatePicker value={editPO.tanggal} data-testid="inv-po-edit-tanggal"
                      onChange={v => setEditPO(f => ({ ...f, tanggal: v }))} />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Supplier</label>
          <SupplierPicker
            suppliers={suppliers}
            value={editPO.supplier_id}
            disabled={sudahAdaPenerimaan}
            onPilih={(id) => {
              const sup = suppliers.find(s => s.id === id);
              setEditPO(f => ({ ...f, supplier_id: id, supplier_nama: sup ? sup.nama : "" }));
            }}
            onSupplierBaru={(sup) => {
              tambahSupplierKeDaftar(setSuppliers, sup);
              setEditPO(f => ({ ...f, supplier_id: sup.id, supplier_nama: sup.nama }));
            }}
            testid="inv-po-edit-supplier"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">No. PO / referensi supplier</label>
          <input value={editPO.no_po_supplier} data-testid="inv-po-edit-nopo" placeholder="opsional"
                 onChange={e => setEditPO(f => ({ ...f, no_po_supplier: e.target.value }))} className={inp} />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Gudang tujuan</label>
          <select value={editPO.warehouse_id} data-testid="inv-po-edit-gudang" disabled={sudahAdaPenerimaan}
                  onChange={e => setEditPO(f => ({ ...f, warehouse_id: e.target.value }))}
                  className={`${inp} ${sudahAdaPenerimaan ? "bg-slate-50 text-slate-500" : ""}`}>
            {gudang.map(g => <option key={g.id} value={g.id}>{g.nama}</option>)}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Skema input</label>
          <select value={editPO.skema} data-testid="inv-po-edit-skema" disabled={sudahAdaPenerimaan}
                  onChange={e => setEditPO(f => ({ ...f, skema: e.target.value }))}
                  className={`${inp} ${sudahAdaPenerimaan ? "bg-slate-50 text-slate-500" : ""}`}>
            <option value="new">Baru — satu harga untuk semua unit</option>
            <option value="second">Second — harga per unit per IMEI</option>
          </select>
          {/* P9: sama seperti FormPO — kolom catatan per unit untuk skema second. */}
          {editPO.skema === "second" && (
            <label className="mt-1.5 flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
              <input type="checkbox" checked={!!editPO.catatan_unit} data-testid="inv-po-edit-catatan-unit"
                     onChange={e => setEditPO(f => ({ ...f, catatan_unit: e.target.checked }))}
                     className="w-3.5 h-3.5 rounded border-slate-300" />
              Tambahkan catatan per unit (kondisi, kelengkapan)
            </label>
          )}
        </div>
        <div>
          {/* Cara bayar TIDAK bisa diubah lewat Edit (brief 22 Sep 2026, H1 —
              backend `edit_po` menguncinya ke nilai tersimpan). Ditampilkan
              hanya-baca supaya PO lama kas/bank tetap jelas asal-usulnya. */}
          <label className="block text-xs font-medium text-slate-600 mb-1">Pembayaran</label>
          <p className="min-h-10 px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 text-xs text-slate-600 flex items-start gap-1.5"
             data-testid="inv-po-edit-info-bayar">
            <Lock className="w-3 h-3 mt-0.5 shrink-0" /> {infoPembayaranPO(editPO)}
          </p>
        </div>
        {(editPO.metode_bayar || "utang") === "utang" && (
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Jatuh tempo <span className="text-slate-400 font-normal">(opsional — wajib paling lambat saat barang diterima)</span>
            </label>
            <DatePicker value={editPO.tanggal_jatuh_tempo} data-testid="inv-po-edit-jatuhtempo"
                        onChange={v => setEditPO(f => ({ ...f, tanggal_jatuh_tempo: v }))} />
          </div>
        )}
      </div>

      {/* PO Barang Trade-in tidak boleh ber-PPN (backend menolaknya) — kotak
          centangnya tidak ditawarkan supaya staf tidak menabrak 400. */}
      {!editPO.barang_tradein && (
        <label className="flex items-center gap-2 text-sm text-slate-700 bg-amber-50 border border-amber-200 rounded-lg p-3 cursor-pointer">
          <input type="checkbox" checked={!!editPO.kena_ppn} data-testid="inv-po-edit-kena-ppn"
                 onChange={e => setEditPO(f => ({ ...f, kena_ppn: e.target.checked }))}
                 className="w-4 h-4 rounded border-slate-300" />
          <span>
            Barang ini <strong>kena PPN {PPN_PERSEN_PREVIEW}%</strong> — harga di atas belum termasuk PPN.
          </span>
        </label>
      )}

      <div className="space-y-2">
        <p className="text-xs font-medium text-slate-600">Barang dipesan</p>
        {editPO.items.map((it, i) => (
          <BarisItem key={i} item={it} idx={i} skema={editPO.skema} onUbah={ubah}
                     tampilCatatan={editPO.skema === "second" && !!editPO.catatan_unit}
                     bisaHapus={editPO.items.length > 1}
                     qtyDiterima={it.qty_diterima || 0} hargaTerkunci={(it.qty_diterima || 0) > 0}
                     onHapus={(idx) => setEditPO(f => ({ ...f, items: f.items.filter((_, j) => j !== idx) }))} />
        ))}
        <button onClick={() => setEditPO(f => ({ ...f, items: [...f.items, { sku: "", qty: 1, harga_per_unit: "", units: [], qty_diterima: 0 }] }))}
                data-testid="inv-po-edit-additem" className="text-sm text-emerald-700 hover:underline">
          + Tambah barang
        </button>
      </div>

      <RingkasanPO items={editPO.items} kenaPPN={editPO.kena_ppn} />
    </Kerangka>
    {konfirmasiSimpan && (
      <KonfirmasiSimpanPO items={editPO.items} kenaPPN={!!editPO.kena_ppn} busy={busy}
                          onKembali={() => setKonfirmasiSimpan(false)}
                          onLanjutkan={() => { setKonfirmasiSimpan(false); onSimpan(); }} />
    )}
    </>
  );
}

// ============ Poin 12 — View: modal pratinjau PO ============
function ModalPreviewPO({ po, loading, onClose, bisaEdit, bisaBatal, onEdit, onBatal, onCetak }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} data-testid="inv-po-preview-modal"
           className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] flex flex-col shadow-2xl">
        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-display font-bold text-lg flex items-center gap-2">
            <Eye className="w-5 h-5 text-emerald-600" /> Detail PO{po ? ` ${po.code}` : ""}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 overflow-y-auto flex-1 space-y-3">
          {loading || !po ? (
            <p className="text-center text-sm text-slate-500 py-12">Memuat detail PO...</p>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><p className="text-[11px] text-slate-500">Tanggal</p><p>{formatTanggal(po.tanggal)}</p></div>
                <div><p className="text-[11px] text-slate-500">Status</p>
                  <span className={`text-[11px] px-2 py-0.5 rounded border inline-flex items-center gap-1 ${(STATUS[po.status] || STATUS.terbuka).c}`}>
                    {(STATUS[po.status] || STATUS.terbuka).l}
                  </span>
                </div>
                <div><p className="text-[11px] text-slate-500">Supplier</p><p>{po.supplier_nama}</p></div>
                <div><p className="text-[11px] text-slate-500">Gudang</p><p>{po.warehouse_nama}</p></div>
                <div><p className="text-[11px] text-slate-500">Pembayaran</p>
                  <p className="text-xs" data-testid="inv-po-preview-bayar">{infoPembayaranPO(po)}</p></div>
                <div><p className="text-[11px] text-slate-500">Jatuh tempo</p><p>{po.tanggal_jatuh_tempo ? formatTanggal(po.tanggal_jatuh_tempo) : "—"}</p></div>
                <div><p className="text-[11px] text-slate-500">Progres</p><p>{po.qty_diterima}/{po.qty_total} unit</p></div>
                <div><p className="text-[11px] text-slate-500">Nilai total</p><p className="font-mono font-semibold">{idr(po.nilai_total)}</p></div>
              </div>

              <div>
                <p className="text-xs font-medium text-slate-600 mb-1">Barang dipesan</p>
                <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
                  {(po.items || []).map((it, i) => (
                    <div key={i} className="px-3 py-2 flex items-center justify-between text-sm" data-testid={`inv-po-preview-item-${i}`}>
                      <div>
                        <p className="font-mono text-[11px] text-slate-500">{it.sku}</p>
                        <p>{it.nama}</p>
                      </div>
                      <div className="text-right">
                        <p>{it.qty_diterima}/{it.qty} unit</p>
                        <p className="text-[11px] text-slate-500">{idr(it.nilai)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {(po.penerimaan || []).length > 0 && (
                <div>
                  <p className="text-xs font-medium text-slate-600 mb-1">Riwayat penerimaan</p>
                  <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
                    {po.penerimaan.map(r => (
                      <div key={r.id} className="px-3 py-2 flex items-center justify-between text-sm" data-testid={`inv-po-preview-penerimaan-${r.id}`}>
                        <div>
                          <p className="font-mono text-[11px] text-slate-500">{r.code}</p>
                          <p className="text-[11px] text-slate-500">{formatTanggal(r.tanggal)}</p>
                        </div>
                        <p className="font-mono">{idr(r.nilai_total)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {po.qty_diterima > 0 && (
                <div className="bg-sky-50 border border-sky-200 rounded-lg p-3 text-xs text-sky-800">
                  PO ini sudah menerima sebagian/seluruh barang — tidak bisa dibatalkan lagi dari sini.
                  Kalau barang yang sudah masuk perlu dikembalikan, pakai menu <strong>Retur Pembelian</strong>.
                </div>
              )}
            </>
          )}
        </div>
        <div className="p-4 border-t border-slate-100 flex justify-end gap-2 flex-wrap">
          <button onClick={onClose} data-testid="inv-po-preview-tutup"
                  className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Tutup</button>
          {po && (
            <button onClick={() => onCetak(po)} data-testid="inv-po-preview-cetak"
                    className="h-10 px-4 rounded-lg border border-slate-200 text-sm inline-flex items-center gap-1.5">
              <Printer className="w-4 h-4" /> Cetak
            </button>
          )}
          {/* Batalkan: SAMA gerbang status dengan tabel utama (cuma "terbuka") —
              backend `batal_po` sebenarnya juga menerima "menunggu_approval",
              tapi UI sengaja tidak menawarkannya di sini supaya konsisten
              dengan tombol yang sudah ada di tabel. */}
          {po && po.status === "terbuka" && bisaBatal && (
            <button onClick={() => onBatal(po)} data-testid="inv-po-preview-batal"
                    className="h-10 px-4 rounded-lg bg-rose-50 hover:bg-rose-100 text-rose-700 text-sm font-medium inline-flex items-center gap-1.5">
              <Trash2 className="w-4 h-4" /> Batalkan
            </button>
          )}
          {po && !["batal", "selesai"].includes(po.status) && bisaEdit && (
            <button onClick={() => onEdit(po)} data-testid="inv-po-preview-edit"
                    className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium inline-flex items-center gap-1.5">
              <Pencil className="w-4 h-4" /> Edit
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function FormHibah({ form, setForm, gudang, busy, onSimpan }) {
  const ubah = (idx, patch) => setForm(f => ({
    ...f, items: f.items.map((it, i) => i === idx ? { ...it, ...patch } : it),
  }));
  return (
    <Kerangka judul="Barang Masuk Tanpa Biaya" ikon={Gift} testid="inv-hibah-modal"
              onClose={() => setForm(null)} onSimpan={onSimpan} busy={busy} label="Catat Barang">
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
        Isi taksiran harga wajarnya, jangan nol. Barang bermodal nol akan membuat seluruh harga jual
        terlihat sebagai laba dan merusak perbandingan margin antar produk.
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Tanggal</label>
          <DatePicker value={form.tanggal} data-testid="inv-hibah-tanggal"
                      onChange={v => setForm(f => ({ ...f, tanggal: v }))} />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Sumber barang</label>
          <select value={form.sumber} data-testid="inv-hibah-sumber"
                  onChange={e => setForm(f => ({ ...f, sumber: e.target.value }))} className={inp}>
            {SUMBER.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Gudang</label>
          <select value={form.warehouse_id} data-testid="inv-hibah-gudang"
                  onChange={e => setForm(f => ({ ...f, warehouse_id: e.target.value }))} className={inp}>
            {gudang.map(g => <option key={g.id} value={g.id}>{g.nama}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Dari siapa / keterangan</label>
          <input value={form.asal_keterangan} data-testid="inv-hibah-asal"
                 onChange={e => setForm(f => ({ ...f, asal_keterangan: e.target.value }))} className={inp} />
        </div>
      </div>
      <div className="space-y-2">
        <p className="text-xs font-medium text-slate-600">Barang yang masuk</p>
        {form.items.map((it, i) => (
          <BarisItem key={i} item={it} idx={i} skema={form.skema} onUbah={ubah}
                     tampilCatatan={form.skema === "second" && !!form.catatan_unit}
                     tampilSN
                     bisaHapus={form.items.length > 1}
                     onHapus={(idx) => setForm(f => ({ ...f, items: f.items.filter((_, j) => j !== idx) }))} />
        ))}
        <button onClick={() => setForm(f => ({ ...f, items: [...f.items, { sku: "", qty: 1, harga_per_unit: "", units: [] }] }))}
                data-testid="inv-hibah-additem" className="text-sm text-emerald-700 hover:underline">
          + Tambah barang
        </button>
      </div>
    </Kerangka>
  );
}

function FormTerima({ po, onClose, onSimpan, busy }) {
  const [tanggal, setTanggal] = useState(hariIniISO());
  const [noDo, setNoDo] = useState("");
  // P10 (brief 4 Sep 2026): PO Tempo yang dibuat tanpa termin mengisi jatuh
  // temponya DI SINI — utangnya lahir saat barang diterima.
  const [jatuhTempo, setJatuhTempo] = useState(po.tanggal_jatuh_tempo || "");
  // PO Barang Trade-in (brief 22 Sep 2026, H1): lunas dari penampung trade-in,
  // tanpa PPN & tanpa biaya tambahan (backend `_validasi_po_tradein` menolak
  // keduanya) — kotak isiannya tidak ditawarkan sama sekali.
  const poTradein = !!po.barang_tradein;
  const [baris, setBaris] = useState(
    po.items.map(i => ({
      sku: i.sku, nama: i.nama, has_imei: i.has_imei,
      // Kolom Serial Number kedua (A9) hanya untuk produk ber-IMEI; produk
      // SN-saja nomornya sudah SN. `identifier_type` ditempel `GET /inventory/po`.
      kolom_sn: i.has_imei && (i.identifier_type || "imei") !== "sn",
      sisa: i.qty - (i.qty_diterima || 0),
      harga_per_unit: i.harga_per_unit || (i.units?.[0]?.harga ?? 0),
      qty: 0,
      units: (i.units || []).map(u => ({ ...u, pilih: false, serial_number: "" })),
    }))
  );
  const ubahUnitTerima = (i, j, patch) => setBaris(s => s.map((x, k) => k === i
    ? { ...x, units: x.units.map((y, l) => l === j ? { ...y, ...patch } : y) }
    : x));
  // Landed cost (brief bos #27 / batch D #2b): ongkir/bea/dsb yang dialokasikan
  // proporsional ke modal barang yang diterima — bukan beban terpisah.
  const [biayaTambahan, setBiayaTambahan] = useState([]);
  const tambahBiaya = () => setBiayaTambahan(b => [...b, { nama: "", nominal: "" }]);
  const ubahBiaya = (i, patch) => setBiayaTambahan(b => b.map((x, j) => j === i ? { ...x, ...patch } : x));
  const hapusBiaya = (i) => setBiayaTambahan(b => b.filter((_, j) => j !== i));

  // PPN (keputusan owner 18 Agu 2026): DO/penerimaan inilah yang SUNGGUH
  // menjurnal — satu faktur pajak supplier menutupi satu DO, bukan satu PO
  // (yang bisa diterima bertahap lewat beberapa DO). Pra-isi dari kena_ppn
  // milik PO (kalau ada, kompat mundur untuk PO lama = false), tapi staf
  // tetap bisa menimpanya di sini sesuai faktur yang benar-benar diterima.
  const [kenaPPN, setKenaPPN] = useState(!!po.kena_ppn);
  const [konfirmasiPPN, setKonfirmasiPPN] = useState(false);
  const [pendingBody, setPendingBody] = useState(null);
  // Dialog PPN terbuka DI ATAS Kerangka di bawahnya — daftarkan sebagai
  // lapisan lebih atas supaya Escape untuk dialog PPN tidak ikut menutup
  // (atau memicu palang konfirmasi) modal Terima Barang di baliknya, yang
  // akan membuang semua centang unit yang sudah ditandai (lihat
  // "LAPISAN PALING ATAS YANG MENANG" di useModalGuard.jsx).
  useModalLayer(konfirmasiPPN);

  const bangunBody = () => {
    const items = baris
      .filter(b => (b.has_imei ? b.units.some(u => u.pilih) : Number(b.qty) > 0))
      .map(b => ({
        sku: b.sku,
        qty: b.has_imei ? 0 : Number(b.qty),
        harga_per_unit: Number(b.harga_per_unit) || 0,
        units: b.has_imei
          // P9: catatan unit dari PO ikut ke body Terima — `inventory_units.catatan`
          // ditulis dari baris TERIMA (lihat purchase.py `_normalisasi_items`),
          // bukan dari dokumen PO; tanpa ini catatan tersimpan di PO lalu lenyap.
          ? b.units.filter(u => u.pilih).map(u => ({
              identifier: u.identifier, harga: Number(u.harga) || 0, catatan: u.catatan || "",
              // SN kedua (A9) hanya dikirim bila diisi — kosong = tidak mengubah
              // apa pun (unit lama yang dihidupkan lagi mempertahankan SN-nya).
              ...(b.kolom_sn && (u.serial_number || "").trim() ? { serial_number: u.serial_number.trim() } : {}),
            }))
          : [],
      }));
    if (!items.length) { toast.error("Tandai dulu barang mana yang benar-benar diterima"); return null; }
    // Baris biaya bernominal tapi tanpa nama dulu dibuang diam-diam oleh filter di bawah —
    // landed cost & utang jadi kurang catat tanpa peringatan. Sekarang ditolak eksplisit.
    const biayaTanpaNama = biayaTambahan.some(b => Number(b.nominal) > 0 && !b.nama.trim());
    if (biayaTanpaNama) { toast.error("Isi nama biaya tambahan yang bernominal, atau hapus barisnya"); return null; }
    const biaya = poTradein ? [] : biayaTambahan
      .filter(b => b.nama.trim() && Number(b.nominal) > 0)
      .map(b => ({ nama: b.nama.trim(), nominal: Number(b.nominal) }));
    // P10: backend menolak juga; cek di sini supaya pesannya langsung jelas.
    if (po.metode_bayar === "utang" && !jatuhTempo) {
      toast.error("Isi tanggal jatuh tempo — PO ini dibayar Tempo dan utangnya lahir saat barang diterima"); return null;
    }
    return { tanggal, no_do_supplier: noDo, items, catatan: "", biaya_tambahan: biaya,
             kena_ppn: poTradein ? false : kenaPPN,
             tanggal_jatuh_tempo: po.metode_bayar === "utang" ? (jatuhTempo || null) : null };
  };

  // Pop-up konfirmasi PPN (brief owner 18 Agu 2026) — "sebelum selesai input
  // ... DO akan muncul pop up untuk memperingatkan staff 'Apakah barang ini
  // kena PPN?'". Tombol "Catat Penerimaan" di Kerangka TIDAK langsung
  // menyimpan; ia menyiapkan body lalu membuka konfirmasi ini dulu.
  const kirim = () => {
    const body = bangunBody();
    if (!body) return;
    // PO Barang Trade-in tidak pernah ber-PPN — pertanyaan "kena PPN?" di sini
    // cuma membuka peluang jawaban yang pasti ditolak backend.
    if (poTradein) { onSimpan(body); return; }
    setPendingBody(body);
    setKonfirmasiPPN(true);
  };
  const lanjutkanKirim = () => {
    setKonfirmasiPPN(false);
    if (pendingBody) onSimpan(pendingBody);
  };

  const dppPratinjau = baris.reduce((acc, b) => {
    if (b.has_imei) return acc + b.units.filter(u => u.pilih).reduce((s, u) => s + (Number(u.harga) || 0), 0);
    return acc + (Number(b.qty) || 0) * (Number(b.harga_per_unit) || 0);
  }, 0) + biayaTambahan.reduce((s, x) => s + (Number(x.nominal) || 0), 0);
  const ppnPratinjau = kenaPPN ? Math.round(dppPratinjau * PPN_PERSEN_PREVIEW / 100) : 0;

  const terimaSemua = () => setBaris(s => s.map(b => ({
    ...b,
    qty: b.has_imei ? b.qty : b.sisa,
    units: b.units.map(u => ({ ...u, pilih: true })),
  })));
  const kosongkanPilihan = () => setBaris(s => s.map(b => ({
    ...b,
    qty: b.has_imei ? b.qty : 0,
    units: b.units.map(u => ({ ...u, pilih: false })),
  })));

  const produkDitandai = baris.filter(b => (b.has_imei ? b.units.some(u => u.pilih) : Number(b.qty) > 0)).length;
  const unitDitandai = baris.reduce((acc, b) => acc + (b.has_imei ? b.units.filter(u => u.pilih).length : (Number(b.qty) || 0)), 0);

  return (
    <>
    <Kerangka judul={`Terima Barang — ${po.code}`} ikon={Truck} testid="inv-terima-modal"
              onClose={onClose} onSimpan={kirim} busy={busy} label="Catat Penerimaan">
      <div className="bg-sky-50 border border-sky-200 rounded-lg p-3 text-xs text-sky-800">
        Centang hanya yang benar-benar sampai. Kalau supplier kirim kurang, sisa PO tetap terbuka
        dan tagihannya hanya sebesar yang diterima.
        {/* Brief 22 Sep 2026 (H1): penerimaan tidak lagi melunasi apa pun
            (kecuali PO lama kas/bank) — staf gudang perlu tahu ke mana
            tagihannya pergi. */}
        <span className="block mt-1" data-testid="inv-terima-info-bayar">{infoPembayaranPO(po)}.</span>
      </div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 bg-slate-50 border border-slate-200 rounded-lg p-3">
        <p className="text-xs text-slate-600">
          <span className="font-semibold text-slate-800">{produkDitandai}</span> dari {baris.length} produk ditandai · {unitDitandai} unit
        </p>
        <div className="flex items-center gap-2">
          <button type="button" onClick={terimaSemua} data-testid="terima-pilih-semua"
                  className="h-8 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium inline-flex items-center gap-1.5">
            <ListChecks className="w-3.5 h-3.5" /> Terima semua sesuai PO
          </button>
          <button type="button" onClick={kosongkanPilihan} data-testid="terima-kosongkan"
                  className="h-8 px-3 rounded-lg border border-slate-200 hover:bg-slate-100 text-slate-700 text-xs font-medium inline-flex items-center gap-1.5">
            <XCircle className="w-3.5 h-3.5" /> Kosongkan pilihan
          </button>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Tanggal terima</label>
          <DatePicker value={tanggal} onChange={setTanggal} data-testid="inv-terima-tanggal" />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">No. surat jalan / DO supplier</label>
          <input value={noDo} onChange={e => setNoDo(e.target.value)}
                 data-testid="inv-terima-nodo" className={inp} />
        </div>
        {po.metode_bayar === "utang" && (
          <div className="col-span-2">
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Jatuh tempo utang {po.tanggal_jatuh_tempo ? "(dari PO, bisa diubah)" : "— wajib, PO ini belum punya termin"}
            </label>
            <DatePicker value={jatuhTempo} onChange={setJatuhTempo} data-testid="inv-terima-jt" />
          </div>
        )}
      </div>

      {baris.map((b, i) => (
        <div key={b.sku} className="border border-slate-200 rounded-lg p-3 space-y-2">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-900">{b.nama}</p>
              <p className="font-mono text-[11px] text-slate-500">{b.sku} · sisa pesanan {b.sisa}</p>
            </div>
            {!b.has_imei && (
              <input type="number" min="0" max={b.sisa} value={b.qty} placeholder="Qty diterima"
                     onChange={e => setBaris(s => s.map((x, j) => j === i ? { ...x, qty: e.target.value } : x))}
                     data-testid={`inv-terima-qty-${b.sku}`} className={`${inp} w-32`} />
            )}
          </div>
          {b.has_imei && (
            <div className="space-y-1">
              {b.kolom_sn && (
                <p className="text-[11px] text-slate-500">
                  Serial Number (SN) dari kotak/perangkat — opsional, dipakai Price Protection &amp; Claim MDR.
                </p>
              )}
              {b.units.map((u, j) => (
                // Bukan <label> pembungkus: kotak SN di dalam label ikut
                // mengaktifkan centang saat diklik di sebagian peramban.
                <div key={j} className="flex items-center gap-2 text-sm flex-wrap">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={u.pilih}
                           onChange={e => ubahUnitTerima(i, j, { pilih: e.target.checked })}
                           data-testid={`inv-terima-unit-${b.sku}-${j}`} className="w-4 h-4 rounded border-slate-300" />
                    <span className="font-mono text-xs">{u.identifier}</span>
                    <span className="text-[11px] text-slate-500">{idr(u.harga)}</span>
                  </label>
                  {b.kolom_sn && (
                    <div className="flex items-center gap-1 ml-auto">
                      <input value={u.serial_number} disabled={!u.pilih}
                             placeholder={u.pilih ? "Serial Number (opsional)" : "centang dulu unitnya"}
                             onChange={e => ubahUnitTerima(i, j, { serial_number: e.target.value })}
                             data-testid={`inv-terima-sn-${b.sku}-${j}`}
                             className={`${inpTetap} h-8 w-48 max-w-full font-mono text-xs ${u.pilih ? "" : "bg-slate-50"}`} />
                      {u.pilih && (
                        <BarcodeScanner onDetected={(v) => ubahUnitTerima(i, j, { serial_number: v })}
                                        testId={`inv-terima-scan-sn-${b.sku}-${j}`} title="Scan Serial Number" />
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}

      {poTradein && (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-600"
             data-testid="inv-terima-tradein-info">
          PO Barang Trade-in: setiap IMEI yang diterima harus ada di daftar tunggu trade-in cabang
          {po.cabang ? <strong> {po.cabang}</strong> : " PO ini"}. Tanpa PPN dan tanpa biaya tambahan —
          ongkir/biaya lain dicatat terpisah lewat Finance.
        </div>
      )}
      {!poTradein && (
      <div className="border border-slate-200 rounded-lg p-3 space-y-2">
        <p className="text-xs font-medium text-slate-600">Biaya Tambahan (ongkir, bea, dll)</p>
        {biayaTambahan.map((b, i) => (
          <div key={i} className="flex items-center gap-2">
            <input value={b.nama} placeholder="mis. Ongkir"
                   onChange={e => ubahBiaya(i, { nama: e.target.value })}
                   data-testid={`po-biaya-nama-${i}`} aria-label={`Nama biaya tambahan ${i + 1}`}
                   className={inp} />
            <div className="w-48 shrink-0">
              <InputRupiah value={b.nominal} testid={`po-biaya-nominal-${i}`}
                           ariaLabel={`Nominal biaya tambahan ${i + 1}`}
                           onChange={v => ubahBiaya(i, { nominal: v })} />
            </div>
            <button type="button" onClick={() => hapusBiaya(i)} data-testid={`po-biaya-hapus-${i}`}
                    className="w-9 h-9 rounded hover:bg-rose-50 text-rose-600 grid place-items-center shrink-0">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
        <button type="button" onClick={tambahBiaya} data-testid="po-biaya-tambah"
                className="text-xs text-emerald-700 hover:underline">+ Tambah biaya</button>
        <p className="text-[11px] text-slate-500">
          Dialokasikan proporsional ke modal barang — bukan beban terpisah.
        </p>
      </div>
      )}

      {/* PPN (keputusan owner 18 Agu 2026): DO/penerimaan inilah yang SUNGGUH
          menjurnal D 13xx Persediaan (DPP saja) + D 1290 PPN Masukan + K Utang
          Usaha/kas-bank (DPP+PPN). Nilai final dihitung ulang di backend
          (hitung_ppn), baris di bawah cuma pratinjau. */}
      {!poTradein && (
        <label className="flex items-center gap-2 text-sm text-slate-700 bg-amber-50 border border-amber-200 rounded-lg p-3 cursor-pointer">
          <input type="checkbox" checked={kenaPPN} data-testid="inv-terima-kena-ppn"
                 onChange={e => setKenaPPN(e.target.checked)}
                 className="w-4 h-4 rounded border-slate-300" />
          <span>
            Penerimaan ini <strong>kena PPN {PPN_PERSEN_PREVIEW}%</strong> — PPN akan dijurnal terpisah
            (PPN Masukan) dan ditambahkan ke tagihan/utang, TIDAK ikut modal barang.
          </span>
        </label>
      )}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 grid grid-cols-3 gap-2">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
            {kenaPPN ? "DPP (Nilai Barang)" : "Total Diterima"}
          </p>
          <p className="text-sm font-bold text-emerald-700 mt-0.5 font-mono" data-testid="inv-terima-dpp">{idr(dppPratinjau)}</p>
        </div>
        {kenaPPN && (
          <>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">PPN {PPN_PERSEN_PREVIEW}%</p>
              <p className="text-sm font-bold text-amber-700 mt-0.5 font-mono" data-testid="inv-terima-ppn">{idr(ppnPratinjau)}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Total Tagihan</p>
              <p className="text-sm font-bold text-slate-900 mt-0.5 font-mono" data-testid="inv-terima-total">{idr(dppPratinjau + ppnPratinjau)}</p>
            </div>
          </>
        )}
      </div>
    </Kerangka>
    {konfirmasiPPN && (
      <KonfirmasiPPN kenaPPN={kenaPPN} busy={busy}
                     onKembali={() => setKonfirmasiPPN(false)}
                     onLanjutkan={lanjutkanKirim} />
    )}
    </>
  );
}
